import * as pdfjsLib from 'pdfjs-dist';
// Inlined into the bundle: a separate worker file breaks when assets are served
// from another origin (ASSET_URL/CDN) — workers must be same-origin.
import PdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker&inline';
import {
    BlendMode,
    PDFCheckBox,
    PDFDocument,
    PDFDropdown,
    PDFField,
    PDFFont,
    PDFPage as PDFLibPage,
    PDFTextField,
    PDFWidgetAnnotation,
    StandardFonts,
    degrees,
    rgb,
} from 'pdf-lib';
import { createWorker, type Worker as TesseractWorker } from 'tesseract.js';
import { showToast } from './toast';
import { csrf } from './csrf';
import { getCurrentUserId } from './checkpointHelper/auth/context';

pdfjsLib.GlobalWorkerOptions.workerPort = new PdfWorker();

/* ============================================================ types */

type FontFamily = 'Helvetica' | 'Times' | 'Courier';

/** Box in page coordinates: PDF points, y measured from the page TOP. */
interface Box {
    x: number;
    y: number;
    w: number;
    h: number;
}

interface BaseEl {
    id: number;
    x: number; // PDF points, from page top-left
    y: number;
    w: number;
    h: number;
    node: HTMLDivElement;
    /** Original location of content extracted from the opened PDF (used to white it out on export). */
    src?: Box;
    /** Extracted content only re-renders into the PDF once it has actually been changed. */
    dirty: boolean;
}

interface TextEl extends BaseEl {
    type: 'text';
    text: string;
    fontSize: number;
    fontFamily: FontFamily;
    bold: boolean;
    italic: boolean;
    color: string;
    whiteBg: boolean;
    /** OCR text layer: drawn at 0 opacity on export (searchable, invisible — the
     *  scanned image underneath is what's actually seen), dimmed in the editor. */
    ocr?: boolean;
    /** Degrees, counterclockwise-positive (pdf-lib/math convention) — used by watermarks. */
    rotation?: number;
    /** 0-1, defaults to fully opaque — used by watermarks. */
    opacity?: number;
}

interface ImageEl extends BaseEl {
    type: 'image';
    dataUrl: string; // always png or jpeg
    isJpeg: boolean;
}

interface ShapeEl extends BaseEl {
    type: 'rect' | 'ellipse';
    fill: string;
    stroke: string;
    strokeWidth: number;
}

/** A black box that permanently removes whatever is beneath it (see pageNeedsFlatten). */
interface RedactEl extends BaseEl {
    type: 'redact';
}

/** A freehand stroke (pen) or highlight (highlighter). Points are local to x/y so moves are free. */
interface PathEl extends BaseEl {
    type: 'path';
    points: { x: number; y: number }[];
    mode: 'pen' | 'highlighter';
    color: string;
    strokeWidth: number;
}

/**
 * A detected AcroForm field, rendered as a real, always-live input/checkbox/
 * select. Exported as flattened static content (see exportPdf) rather than a
 * live PDF form field — see apps/www/_docs/pdf_editor_form_filling_todo.md
 * for what keeping fields interactive in the export would require.
 */
interface FieldEl extends BaseEl {
    type: 'field';
    fieldType: 'text' | 'checkbox' | 'dropdown';
    name: string;
    value: string;
    checked?: boolean;
    options?: string[];
    fontSize: number;
}

type El = TextEl | ImageEl | ShapeEl | RedactEl | PathEl | FieldEl;

interface Page {
    width: number; // PDF points
    height: number;
    srcIndex: number | null; // page index in the originally opened PDF, null = new blank page
    elements: El[];
    /** Regions of deleted original content that must be whited out on export. */
    erasures: Box[];
    node: HTMLDivElement;
    bgCanvas: HTMLCanvasElement;
    elLayer: HTMLDivElement;
    /**
     * True for a page inserted from a second PDF (see insertPdfPages). Its
     * srcIndex is always null (it isn't a page of state.pdfjsDoc, so it can
     * never take the copy-original-page export fast path) — instead it always
     * exports by drawing its own erased bgCanvas as a raster background, with
     * every element (including untouched extracted ones) redrawn on top. See
     * pageNeedsFlatten, which folds this in so that path is used unconditionally.
     */
    rasterBg?: boolean;
}

export interface DocumentMeta {
    id: number;
    name: string;
    size: number;
    updated_at: string;
}

/** Params the "editor" view is mounted with — see views.ts / dashboard.ts. */
export interface EditorMountParams {
    action?: 'new' | 'open-bytes';
    size?: readonly number[];
    bytes?: Uint8Array;
    name?: string;
    docId?: number | null;
    /** Present when opening a document via a public share link instead of ownership. */
    shareToken?: string | null;
    sharePermission?: 'view' | 'edit' | null;
}

type Tool = 'text' | 'image' | 'rect' | 'ellipse' | 'redact' | 'pen' | 'highlighter';

/* ============================================================ state */

export const PAGE_SIZES = { letter: [612, 792], a4: [595.28, 841.89] } as const;
const RENDER_SCALE = 2; // backing-store resolution for pdf.js renders
const TEXT_PAD = 1; // CSS padding (in points) inside text elements
const BASELINE_RATIO = 0.92; // distance from a text line-box top to its baseline, in ems

const state = {
    pages: [] as Page[],
    originalPdfBytes: null as Uint8Array | null,
    pdfjsDoc: null as pdfjsLib.PDFDocumentProxy | null,
    selected: null as El | null,
    editing: null as TextEl | null,
    pendingTool: null as Tool | null,
    pendingImage: null as { dataUrl: string; isJpeg: boolean; w: number; h: number } | null,
    zoom: 1,
    docId: null as number | null,
    /** Set when the document was opened via a public share link — see docApiUrl(). */
    shareToken: null as string | null,
    /** 'edit' | 'view' when opened via a share (own documents are always full access). */
    sharePermission: null as 'view' | 'edit' | null,
    nextId: 1,
    defaultPageSize: PAGE_SIZES.letter as readonly [number, number] | readonly number[],
};

const defaults = {
    fontSize: 16,
    fontFamily: 'Helvetica' as FontFamily,
    bold: false,
    italic: false,
    color: '#111111',
    whiteBg: false,
    fill: '#e2e8f0',
    stroke: '#334155',
    strokeWidth: 1,
};

const PEN_DEFAULTS = { color: '#111111', strokeWidth: 2 };
const HIGHLIGHTER_DEFAULTS = { color: '#ffeb3b', strokeWidth: 14, opacity: 0.55 };

/* ============================================================ dom helpers */

function $<T extends HTMLElement>(sel: string): T {
    const el = document.querySelector<T>(sel);
    if (!el) throw new Error(`missing element ${sel}`);
    return el;
}

// These are (re)acquired in mountEditor() each time the editor view is
// rendered, since its container is rebuilt fresh on every navigation.
let pagesHost: HTMLDivElement;
let docNameInput: HTMLInputElement;
let hintBar: HTMLDivElement;

function setHint(msg: string | null): void {
    hintBar.classList.toggle('hidden', !msg);
    hintBar.textContent = msg ?? '';
}

const CSS_FONT: Record<FontFamily, string> = {
    Helvetica: 'Helvetica, Arial, sans-serif',
    Times: '"Times New Roman", Times, serif',
    Courier: '"Courier New", Courier, monospace',
};

function hexToRgb(hex: string): { r: number; g: number; b: number } {
    const n = parseInt(hex.slice(1), 16);
    return { r: ((n >> 16) & 255) / 255, g: ((n >> 8) & 255) / 255, b: (n & 255) / 255 };
}

function markDirty(el: El): void {
    el.dirty = true;
}

/** SVG path `d` string through the given points, smoothed via quadratic curves through midpoints. */
function buildSmoothPath(points: { x: number; y: number }[]): string {
    if (points.length === 0) return '';
    if (points.length === 1) return `M ${points[0].x} ${points[0].y} L ${points[0].x} ${points[0].y}`;
    let d = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length - 1; i++) {
        const mx = (points[i].x + points[i + 1].x) / 2;
        const my = (points[i].y + points[i + 1].y) / 2;
        d += ` Q ${points[i].x} ${points[i].y} ${mx} ${my}`;
    }
    const last = points[points.length - 1];
    d += ` L ${last.x} ${last.y}`;
    return d;
}

/* ============================================================ history (undo/redo) */

type Snap = Record<string, unknown>;

type Command =
    | { kind: 'el-change'; el: El; before: Snap; after: Snap; keys: string; time: number }
    | { kind: 'el-create'; el: El; page: Page }
    | { kind: 'el-delete'; el: El; page: Page; index: number }
    | { kind: 'page-add'; page: Page; index: number }
    | { kind: 'page-delete'; page: Page; index: number }
    | { kind: 'page-reorder'; from: number; to: number };

const history: Command[] = [];
let historyIndex = 0;
const HISTORY_LIMIT = 200;

let undoBtn: HTMLButtonElement;
let redoBtn: HTMLButtonElement;

const MUTABLE_KEYS: Record<El['type'], readonly string[]> = {
    text: ['x', 'y', 'w', 'h', 'dirty', 'text', 'fontSize', 'fontFamily', 'bold', 'italic', 'color', 'whiteBg', 'rotation', 'opacity'],
    image: ['x', 'y', 'w', 'h', 'dirty'],
    rect: ['x', 'y', 'w', 'h', 'dirty', 'fill', 'stroke', 'strokeWidth'],
    ellipse: ['x', 'y', 'w', 'h', 'dirty', 'fill', 'stroke', 'strokeWidth'],
    redact: ['x', 'y', 'w', 'h', 'dirty'],
    path: ['x', 'y', 'w', 'h', 'dirty', 'color', 'strokeWidth'],
    field: ['x', 'y', 'w', 'h', 'dirty', 'value', 'checked'],
};

function snapshotEl(el: El): Snap {
    const snap: Snap = {};
    for (const k of MUTABLE_KEYS[el.type]) snap[k] = (el as unknown as Snap)[k];
    return snap;
}

function applySnapshot(el: El, snap: Snap): void {
    Object.assign(el, snap);
    syncElNode(el);
    if (state.selected === el) reflectSelectionInToolbar(el);
}

function updateHistoryButtons(): void {
    undoBtn.disabled = historyIndex === 0;
    redoBtn.disabled = historyIndex === history.length;
}

function pushCommand(cmd: Command): void {
    history.splice(historyIndex); // a new action discards the redo branch
    history.push(cmd);
    if (history.length > HISTORY_LIMIT) history.shift();
    historyIndex = history.length;
    updateHistoryButtons();
    scheduleStripRefresh();
    scheduleAutosave();
}

/** Record a property change; rapid tweaks of the same properties coalesce into one step. */
function pushElChange(el: El, before: Snap): void {
    const after = snapshotEl(el);
    const keys = Object.keys(after).filter((k) => after[k] !== before[k]);
    if (keys.length === 0) return;
    const keySig = keys.join();
    const prev = history[historyIndex - 1];
    if (prev?.kind === 'el-change' && prev.el === el && prev.keys === keySig && performance.now() - prev.time < 1000) {
        prev.after = after;
        prev.time = performance.now();
        return;
    }
    pushCommand({ kind: 'el-change', el, before, after, keys: keySig, time: performance.now() });
}

function reattachElements(page: Page): void {
    for (const e of page.elements) page.elLayer.appendChild(e.node);
}

function restoreEl(page: Page, el: El, index: number): void {
    page.elements.splice(Math.min(index, page.elements.length), 0, el);
    if (el.src) {
        const i = page.erasures.indexOf(el.src);
        if (i >= 0) page.erasures.splice(i, 1);
    }
    reattachElements(page);
    syncElNode(el);
}

function removePage(page: Page): void {
    if (state.selected && page.elements.includes(state.selected)) select(null);
    state.pages.splice(state.pages.indexOf(page), 1);
    rebuildPages();
}

function insertPage(page: Page, index: number): void {
    state.pages.splice(Math.min(index, state.pages.length), 0, page);
    rebuildPages();
}

function movePage(from: number, to: number): void {
    const [page] = state.pages.splice(from, 1);
    state.pages.splice(to, 0, page);
    rebuildPages();
    buildPageStrip();
}

function reorderPageWithHistory(from: number, to: number): void {
    if (from === to) return;
    movePage(from, to);
    pushCommand({ kind: 'page-reorder', from, to });
    state.pages[to].node.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function runCommand(cmd: Command, dir: 'undo' | 'redo'): void {
    const un = dir === 'undo';
    switch (cmd.kind) {
        case 'el-change':
            applySnapshot(cmd.el, un ? cmd.before : cmd.after);
            break;
        case 'el-create':
            if (un) removeEl(cmd.el);
            else restoreEl(cmd.page, cmd.el, cmd.page.elements.length);
            break;
        case 'el-delete':
            if (un) restoreEl(cmd.page, cmd.el, cmd.index);
            else removeEl(cmd.el);
            break;
        case 'page-add':
            if (un) removePage(cmd.page);
            else insertPage(cmd.page, cmd.index);
            break;
        case 'page-delete':
            if (un) insertPage(cmd.page, cmd.index);
            else removePage(cmd.page);
            break;
        case 'page-reorder':
            if (un) movePage(cmd.to, cmd.from);
            else movePage(cmd.from, cmd.to);
            break;
    }
}

function undo(): void {
    if (historyIndex === 0) return;
    stopEditing();
    runCommand(history[--historyIndex], 'undo');
    updateHistoryButtons();
    scheduleStripRefresh();
    scheduleAutosave();
}

function redo(): void {
    if (historyIndex === history.length) return;
    stopEditing();
    runCommand(history[historyIndex++], 'redo');
    updateHistoryButtons();
    scheduleStripRefresh();
    scheduleAutosave();
}

function resetHistory(): void {
    history.length = 0;
    historyIndex = 0;
    lastSavedHistoryIndex = 0;
    clearTimeout(autosaveTimer);
    updateHistoryButtons();
}

/* ============================================================ page rendering */

function pageTemplate(page: Page, index: number): void {
    const node = page.node;
    node.className = 'pdf-page';
    node.innerHTML = '';
    node.style.width = `${page.width * state.zoom}px`;
    node.style.height = `${page.height * state.zoom}px`;

    const label = document.createElement('div');
    label.className = 'page-label';
    label.innerHTML = `<span>Page ${index + 1}</span>`;
    const ocrBtn = document.createElement('button');
    ocrBtn.textContent = 'Make searchable (OCR)';
    ocrBtn.title = 'Recognize text on this page (useful for scanned pages) and add it as an invisible, searchable/selectable layer';
    ocrBtn.addEventListener('click', () => void ocrPage(page, ocrBtn));
    label.appendChild(ocrBtn);
    if (state.pages.length > 1) {
        const del = document.createElement('button');
        del.className = 'page-label-delete';
        del.textContent = '✕ delete';
        del.addEventListener('click', () => deletePage(page));
        label.appendChild(del);
    }
    node.appendChild(label);

    node.appendChild(page.bgCanvas);
    page.bgCanvas.className = 'pdf-bg';

    page.elLayer.className = 'el-layer';
    node.appendChild(page.elLayer);
    for (const el of page.elements) {
        page.elLayer.appendChild(el.node);
        syncElNode(el);
    }

    node.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return; // right-click is handled by the contextmenu listener
        if (e.target === node || e.target === page.elLayer) {
            if (state.pendingTool === 'pen' || state.pendingTool === 'highlighter') {
                startFreehandDraw(page, state.pendingTool, e, node);
            } else if (state.pendingTool) {
                const rect = node.getBoundingClientRect();
                placePendingTool(page, (e.clientX - rect.left) / state.zoom, (e.clientY - rect.top) / state.zoom);
            } else {
                select(null);
            }
        }
    });

    node.addEventListener('contextmenu', (e) => {
        // Only when the empty page (or element layer) is the direct target — an
        // element's own listener handles right-clicks on elements.
        if (e.target === node || e.target === page.elLayer) {
            e.preventDefault();
            openContextForPage(page, e);
        }
    });
}

function rebuildPages(): void {
    pagesHost.innerHTML = '';
    state.pages.forEach((page, i) => {
        pageTemplate(page, i);
        pagesHost.appendChild(page.node);
    });
}

async function renderPageBackground(page: Page): Promise<void> {
    const canvas = page.bgCanvas;
    canvas.width = Math.floor(page.width * RENDER_SCALE);
    canvas.height = Math.floor(page.height * RENDER_SCALE);
    const ctx = canvas.getContext('2d')!;

    if (page.srcIndex !== null && state.pdfjsDoc) {
        const pdfPage = await state.pdfjsDoc.getPage(page.srcIndex + 1);
        const viewport = pdfPage.getViewport({ scale: RENDER_SCALE });
        await pdfPage.render({ canvas, canvasContext: ctx, viewport }).promise;
        try {
            await convertPageToElements(page, pdfPage, ctx);
        } catch (err) {
            console.error('content extraction failed for page', page.srcIndex, err);
        }
    } else {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
}

/**
 * Same render + extract pipeline as renderPageBackground, but against a page
 * from a *second*, temporary pdf.js document (see insertPdfPages) instead of
 * state.pdfjsDoc — used for pages merged in from another PDF, which are never
 * tracked as part of the primary opened document.
 */
async function renderMergedPageBackground(page: Page, pdfPage: pdfjsLib.PDFPageProxy): Promise<void> {
    const canvas = page.bgCanvas;
    canvas.width = Math.floor(page.width * RENDER_SCALE);
    canvas.height = Math.floor(page.height * RENDER_SCALE);
    const ctx = canvas.getContext('2d')!;
    const viewport = pdfPage.getViewport({ scale: RENDER_SCALE });
    await pdfPage.render({ canvas, canvasContext: ctx, viewport }).promise;
    try {
        await convertPageToElements(page, pdfPage, ctx);
    } catch (err) {
        console.error('content extraction failed for merged page', err);
    }
}

/* ============================================================ PDF → HTML conversion
 *
 * Every text run and image on an opened PDF page is converted into a real
 * editable overlay element. The pixels it produced in the rendered canvas are
 * erased (the canvas keeps everything else: table borders, vector art, fills),
 * so the user sees exactly one — editable — copy of each piece of content.
 */

function eraseBox(ctx: CanvasRenderingContext2D, box: Box): void {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(box.x * RENDER_SCALE, box.y * RENDER_SCALE, box.w * RENDER_SCALE, box.h * RENDER_SCALE);
}

async function convertPageToElements(
    page: Page,
    pdfPage: pdfjsLib.PDFPageProxy,
    ctx: CanvasRenderingContext2D,
): Promise<void> {
    await extractImages(page, pdfPage, ctx);
    await extractText(page, pdfPage, ctx);
}

/* ---------------- text extraction ---------------- */

interface TextRun {
    str: string;
    x: number;
    baseline: number; // PDF user space (from page bottom)
    fontSize: number;
    width: number;
    fontName: string;
}

async function extractText(page: Page, pdfPage: pdfjsLib.PDFPageProxy, ctx: CanvasRenderingContext2D): Promise<void> {
    const content = await pdfPage.getTextContent();
    const runs: TextRun[] = [];

    for (const item of content.items) {
        if (!('str' in item) || !item.str.trim()) continue;
        const [a, b, , d, ex, fy] = item.transform as number[];
        const fontSize = Math.hypot(b, d) || Math.abs(d) || Math.abs(a);
        if (!fontSize || fontSize < 1) continue;

        // Merge fragments that sit on the same baseline into one editable run.
        const prev = runs[runs.length - 1];
        const gap = prev ? ex - (prev.x + prev.width) : Infinity;
        if (
            prev &&
            Math.abs(prev.baseline - fy) < 2 &&
            Math.abs(prev.fontSize - fontSize) < 0.6 &&
            gap > -fontSize * 0.3 &&
            gap < fontSize * 0.9
        ) {
            if (gap > fontSize * 0.14 && !prev.str.endsWith(' ') && !item.str.startsWith(' ')) prev.str += ' ';
            prev.str += item.str;
            prev.width = ex + item.width - prev.x;
        } else {
            runs.push({ str: item.str, x: ex, baseline: fy, fontSize, width: item.width, fontName: item.fontName });
        }
    }

    for (const run of runs) {
        const style = content.styles[run.fontName];
        let family: FontFamily = 'Helvetica';
        if (style?.fontFamily?.includes('serif') && !style.fontFamily.includes('sans')) family = 'Times';
        if (style?.fontFamily?.includes('monospace')) family = 'Courier';

        let bold = false;
        let italic = false;
        try {
            const font = pdfPage.commonObjs.get(run.fontName) as { name?: string } | null;
            const name = font?.name ?? '';
            bold = /bold|black|heavy|semibold/i.test(name);
            italic = /italic|oblique/i.test(name);
        } catch {
            /* font object not resolved — keep regular */
        }

        const src: Box = {
            x: run.x - 2,
            y: page.height - run.baseline - run.fontSize * 0.95,
            w: run.width + 4,
            h: run.fontSize * 1.3,
        };
        eraseBox(ctx, src);

        const el = createTextEl(page, {
            x: run.x - TEXT_PAD,
            y: page.height - run.baseline - run.fontSize * BASELINE_RATIO - TEXT_PAD,
            text: run.str,
            fontSize: run.fontSize,
        });
        el.fontFamily = family;
        el.bold = bold;
        el.italic = italic;
        el.color = '#111111';
        el.src = src;
        el.dirty = false;
        syncElNode(el);
    }
}

/* ---------------- image extraction ---------------- */

type Matrix = [number, number, number, number, number, number];

function matMul(m1: Matrix, m2: Matrix): Matrix {
    return [
        m1[0] * m2[0] + m1[2] * m2[1],
        m1[1] * m2[0] + m1[3] * m2[1],
        m1[0] * m2[2] + m1[2] * m2[3],
        m1[1] * m2[2] + m1[3] * m2[3],
        m1[0] * m2[4] + m1[2] * m2[5] + m1[4],
        m1[1] * m2[4] + m1[3] * m2[5] + m1[5],
    ];
}

/** Bounding box (page coords, y from top) of the unit square under a transform. */
function unitSquareBox(m: Matrix, pageHeight: number): Box {
    const xs = [m[4], m[0] + m[4], m[2] + m[4], m[0] + m[2] + m[4]];
    const ys = [m[5], m[1] + m[5], m[3] + m[5], m[1] + m[3] + m[5]];
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    return { x: minX, y: pageHeight - maxY, w: maxX - minX, h: maxY - minY };
}

interface PdfjsImage {
    width: number;
    height: number;
    bitmap?: ImageBitmap;
    data?: Uint8ClampedArray | Uint8Array;
    kind?: number;
}

function imageObjToDataUrl(obj: PdfjsImage): string | null {
    const c = document.createElement('canvas');
    c.width = obj.width;
    c.height = obj.height;
    const cctx = c.getContext('2d')!;

    if (obj.bitmap) {
        cctx.drawImage(obj.bitmap, 0, 0, obj.width, obj.height);
        return c.toDataURL('image/png');
    }
    if (!obj.data) return null;

    const out = cctx.createImageData(obj.width, obj.height);
    const n = obj.width * obj.height;
    if (obj.kind === 3 && obj.data.length >= n * 4) {
        out.data.set(obj.data.subarray(0, n * 4));
    } else if (obj.kind === 2 && obj.data.length >= n * 3) {
        for (let i = 0; i < n; i++) {
            out.data[i * 4] = obj.data[i * 3];
            out.data[i * 4 + 1] = obj.data[i * 3 + 1];
            out.data[i * 4 + 2] = obj.data[i * 3 + 2];
            out.data[i * 4 + 3] = 255;
        }
    } else {
        return null; // 1-bit masks and exotic formats stay in the background canvas
    }
    cctx.putImageData(out, 0, 0);
    return c.toDataURL('image/png');
}

/** Resolve a pdf.js image object, waiting for it if the worker hasn't delivered it yet. */
function getPdfImageObject(pdfPage: pdfjsLib.PDFPageProxy, name: string, timeoutMs = 5000): Promise<PdfjsImage | null> {
    return new Promise((resolve) => {
        const timer = setTimeout(() => resolve(null), timeoutMs);
        try {
            // Reused (cross-page) images live in commonObjs, page-local ones in objs.
            const store = name.startsWith('g_') ? pdfPage.commonObjs : pdfPage.objs;
            store.get(name, (obj: unknown) => {
                clearTimeout(timer);
                resolve((obj ?? null) as PdfjsImage | null);
            });
        } catch {
            clearTimeout(timer);
            resolve(null);
        }
    });
}

async function extractImages(
    page: Page,
    pdfPage: pdfjsLib.PDFPageProxy,
    ctx: CanvasRenderingContext2D,
): Promise<void> {
    const OPS = pdfjsLib.OPS;
    const opList = await pdfPage.getOperatorList();

    let ctm: Matrix = [1, 0, 0, 1, 0, 0];
    const stack: Matrix[] = [];
    const found: { obj: PdfjsImage; box: Box }[] = [];

    for (let i = 0; i < opList.fnArray.length; i++) {
        const fn = opList.fnArray[i];
        const args = opList.argsArray[i];
        if (fn === OPS.save) {
            stack.push(ctm);
        } else if (fn === OPS.restore) {
            ctm = stack.pop() ?? ctm;
        } else if (fn === OPS.transform) {
            ctm = matMul(ctm, args as Matrix);
        } else if (fn === OPS.paintImageXObject || fn === OPS.paintInlineImageXObject) {
            const obj: PdfjsImage | null =
                fn === OPS.paintInlineImageXObject
                    ? (args[0] as PdfjsImage)
                    : await getPdfImageObject(pdfPage, args[0] as string);
            if (!obj?.width || !obj.height) continue;
            const box = unitSquareBox(ctm, page.height);
            if (box.w < 4 || box.h < 4) continue;
            found.push({ obj, box });
        }
    }

    for (const { obj, box } of found) {
        const dataUrl = imageObjToDataUrl(obj);
        if (!dataUrl) continue; // could not decode: keep it visible in the canvas

        eraseBox(ctx, box);
        const el: ImageEl = {
            id: state.nextId++,
            type: 'image',
            x: box.x,
            y: box.y,
            w: box.w,
            h: box.h,
            dataUrl,
            isJpeg: false,
            node: document.createElement('div'),
            src: { ...box },
            dirty: false,
        };
        makeElNode(el);
        page.elements.push(el);
        page.elLayer.appendChild(el.node);
        syncElNode(el);
    }
}

/* ---------------- form field extraction ----------------
 *
 * Same lift-into-an-editable-overlay approach as extractText/extractImages,
 * just sourced from pdf-lib's AcroForm API instead of pdf.js's text/image
 * content, since pdf.js doesn't expose form field structure. An untouched
 * field's src+dirty=false means the field is left alone on export exactly
 * like untouched extracted text — the original page (and its live, fillable
 * field) is copied verbatim. Only fields the user actually fills get their
 * page flattened and their value baked in as static content — see
 * FieldEl and the 'field' case in exportPdf.
 */

/**
 * Replicates PDFForm's own (private) widget→page resolution using only
 * public pdf-lib APIs: prefer the widget's own /P page reference, falling
 * back to scanning every page's /Annots for a matching ref.
 */
function findWidgetPage(pdfDoc: PDFDocument, widget: PDFWidgetAnnotation): PDFLibPage | undefined {
    const pageRef = widget.P();
    let page = pdfDoc.getPages().find((p) => p.ref === pageRef);
    if (!page) {
        const widgetRef = pdfDoc.context.getObjectRef(widget.dict);
        if (widgetRef) page = pdfDoc.findPageForAnnotationRef(widgetRef);
    }
    return page;
}

/**
 * Detects AcroForm fields in the given PDF bytes and lifts each supported
 * widget into a FieldEl on the matching page, erasing its rectangle from the
 * page's raster (same as extractText/extractImages) so nothing but the live
 * overlay control is shown. Text fields, checkboxes, and dropdowns are
 * supported; radio groups, option lists, signatures, and buttons are skipped
 * (see apps/www/_docs/pdf_editor_form_filling_todo.md for the full picture).
 */
async function detectFormFields(bytes: Uint8Array, pages: Page[]): Promise<void> {
    let pdfDoc: PDFDocument;
    try {
        pdfDoc = await PDFDocument.load(bytes);
    } catch (err) {
        console.error('could not parse PDF for form field detection', err);
        return;
    }

    let fields: PDFField[];
    try {
        fields = pdfDoc.getForm().getFields();
    } catch (err) {
        console.error('could not read AcroForm', err);
        return;
    }
    if (fields.length === 0) return;

    const pdfPages = pdfDoc.getPages();

    for (const field of fields) {
        let widgets: PDFWidgetAnnotation[];
        try {
            widgets = field.acroField.getWidgets();
        } catch (err) {
            console.error('could not read widgets for field', field.getName(), err);
            continue;
        }

        for (const widget of widgets) {
            try {
                const pdfPage = findWidgetPage(pdfDoc, widget);
                if (!pdfPage) continue;
                const pageIndex = pdfPages.indexOf(pdfPage);
                const page = pages[pageIndex];
                if (!page) continue;

                const rect = widget.getRectangle();
                if (rect.width < 2 || rect.height < 2) continue;
                const box: Box = { x: rect.x, y: page.height - rect.y - rect.height, w: rect.width, h: rect.height };
                const fontSize = Math.min(Math.max(box.h * 0.65, 6), 18);
                const name = field.getName();

                let el: FieldEl | null = null;
                if (field instanceof PDFTextField) {
                    el = {
                        id: state.nextId++, type: 'field', fieldType: 'text', name,
                        value: field.getText() ?? '', fontSize,
                        x: box.x, y: box.y, w: box.w, h: box.h,
                        node: document.createElement('div'), src: { ...box }, dirty: false,
                    };
                } else if (field instanceof PDFCheckBox) {
                    el = {
                        id: state.nextId++, type: 'field', fieldType: 'checkbox', name,
                        value: '', checked: field.isChecked(), fontSize,
                        x: box.x, y: box.y, w: box.w, h: box.h,
                        node: document.createElement('div'), src: { ...box }, dirty: false,
                    };
                } else if (field instanceof PDFDropdown) {
                    el = {
                        id: state.nextId++, type: 'field', fieldType: 'dropdown', name,
                        value: field.getSelected()[0] ?? '', options: field.getOptions(), fontSize,
                        x: box.x, y: box.y, w: box.w, h: box.h,
                        node: document.createElement('div'), src: { ...box }, dirty: false,
                    };
                }
                // Radio groups, option lists, signatures, and buttons: out of scope for v1.
                if (!el) continue;

                eraseBox(page.bgCanvas.getContext('2d')!, box);
                makeElNode(el);
                page.elements.push(el);
                page.elLayer.appendChild(el.node);
                syncElNode(el);
            } catch (err) {
                console.error('could not extract a form field widget', err);
            }
        }
    }
}

/* ============================================================ elements */

function syncElNode(el: El): void {
    const z = state.zoom;
    const s = el.node.style;
    s.left = `${el.x * z}px`;
    s.top = `${el.y * z}px`;

    if (el.type === 'text') {
        s.width = 'auto';
        s.height = 'auto';
        const content = el.node.querySelector<HTMLDivElement>('.pel-content')!;
        const cs = content.style;
        cs.fontFamily = CSS_FONT[el.fontFamily];
        cs.fontSize = `${el.fontSize * z}px`;
        cs.fontWeight = el.bold ? '700' : '400';
        cs.fontStyle = el.italic ? 'italic' : 'normal';
        cs.color = el.color;
        cs.padding = `${TEXT_PAD * z}px`;
        el.node.style.background = el.whiteBg ? '#ffffff' : 'transparent';
        el.node.style.opacity = String(el.opacity ?? 1);
        el.node.classList.toggle('pel-ocr', !!el.ocr);
        // CSS rotate() is clockwise-positive; el.rotation follows pdf-lib's
        // counterclockwise-positive convention, so the sign flips here. A plain
        // transform doesn't affect offsetWidth/offsetHeight, so measureText below
        // still reads the correct unrotated box.
        el.node.style.transform = el.rotation ? `rotate(${-el.rotation}deg)` : '';
        if (content.innerText !== el.text) content.innerText = el.text;
        measureText(el);
    } else {
        s.width = `${el.w * z}px`;
        s.height = `${el.h * z}px`;
        if (el.type === 'rect' || el.type === 'ellipse') {
            const content = el.node.querySelector<HTMLDivElement>('.pel-content')!;
            content.style.background = el.fill;
            content.style.border = el.strokeWidth > 0 ? `${el.strokeWidth * z}px solid ${el.stroke}` : 'none';
            content.style.borderRadius = el.type === 'ellipse' ? '50%' : '0';
        } else if (el.type === 'path') {
            // The SVG's viewBox matches el.w/el.h exactly (same units as the CSS box,
            // before zoom), so it auto-scales with the container — stroke width and
            // point coordinates stay in plain page points, no manual zoom math needed.
            const svg = el.node.querySelector<SVGSVGElement>('svg')!;
            const path = el.node.querySelector<SVGPathElement>('path')!;
            svg.setAttribute('viewBox', `0 0 ${el.w} ${el.h}`);
            path.setAttribute('d', buildSmoothPath(el.points));
            path.setAttribute('stroke', el.color);
            path.setAttribute('stroke-width', String(el.strokeWidth));
            path.setAttribute('opacity', el.mode === 'highlighter' ? String(HIGHLIGHTER_DEFAULTS.opacity) : '1');
        } else if (el.type === 'field') {
            const control = el.node.querySelector<HTMLElement>('.pel-field-control');
            if (control) control.style.fontSize = `${el.fontSize * z}px`;
        }
        // image: styled entirely by the <img> tag; redact: styled entirely by CSS (.pel-redact).
    }
}

function measureText(el: TextEl): void {
    el.w = el.node.offsetWidth / state.zoom;
    el.h = el.node.offsetHeight / state.zoom;
}

function makeElNode(el: El): void {
    const node = el.node;
    node.className = `pel pel-${el.type === 'text' ? 'text' : el.type === 'image' ? 'img' : el.type === 'redact' ? 'redact' : el.type === 'path' ? 'path' : el.type === 'field' ? 'field' : 'shape'}`;

    if (el.type === 'text') {
        const content = document.createElement('div');
        content.className = 'pel-content';
        content.innerText = el.text;
        node.appendChild(content);
        node.addEventListener('dblclick', () => startEditing(el));
    } else if (el.type === 'image') {
        const img = document.createElement('img');
        img.src = el.dataUrl;
        img.draggable = false;
        node.appendChild(img);
    } else if (el.type === 'redact') {
        // Editor view stays semi-opaque with a label so the user can tell what's
        // covered while working; export draws it fully opaque black (see exportPdf).
        const content = document.createElement('div');
        content.className = 'pel-content';
        content.textContent = 'REDACTED';
        node.appendChild(content);
    } else if (el.type === 'path') {
        node.classList.toggle('pel-highlighter', el.mode === 'highlighter');
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('fill', 'none');
        path.setAttribute('stroke-linecap', 'round');
        path.setAttribute('stroke-linejoin', 'round');
        svg.appendChild(path);
        node.appendChild(svg);
    } else if (el.type === 'field') {
        // A single click selects/drags the box like any other element (see the
        // pointerdown handler below); double-click "activates" the control for
        // typing/checking/choosing, mirroring text's click-to-select vs
        // double-click-to-edit split.
        const content = document.createElement('div');
        content.className = 'pel-content pel-field-content';
        let control: HTMLInputElement | HTMLSelectElement;
        if (el.fieldType === 'text') {
            const input = document.createElement('input');
            input.type = 'text';
            input.value = el.value;
            input.addEventListener('input', () => {
                el.value = input.value;
                markDirty(el);
            });
            control = input;
        } else if (el.fieldType === 'checkbox') {
            const input = document.createElement('input');
            input.type = 'checkbox';
            input.checked = !!el.checked;
            input.addEventListener('change', () => {
                el.checked = input.checked;
                markDirty(el);
            });
            control = input;
        } else {
            const select = document.createElement('select');
            for (const opt of el.options ?? []) {
                const o = document.createElement('option');
                o.value = opt;
                o.textContent = opt;
                select.appendChild(o);
            }
            select.value = el.value;
            select.addEventListener('change', () => {
                el.value = select.value;
                markDirty(el);
            });
            control = select;
        }
        control.className = 'pel-field-control';
        control.addEventListener('blur', () => stopEditingField(el));
        content.appendChild(control);
        node.appendChild(content);
    } else {
        const content = document.createElement('div');
        content.className = 'pel-content';
        node.appendChild(content);
    }

    // Manual double-press detection: preventDefault on pointerdown (needed for
    // dragging) makes native dblclick unreliable in some browsers.
    let lastDown = 0;
    node.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return; // let right-click through to the contextmenu handler
        if (state.editing === el) return;
        if (editingField?.el === el) return;
        if ((e.target as HTMLElement).classList.contains('h')) return;
        e.stopPropagation();
        e.preventDefault();
        const now = performance.now();
        if ((el.type === 'text' || el.type === 'field') && now - lastDown < 400) {
            lastDown = 0;
            if (el.type === 'text') startEditing(el);
            else startEditingField(el);
            return;
        }
        lastDown = now;
        select(el);
        startDrag(el, e);
    });

    node.addEventListener('contextmenu', (e) => {
        if (state.editing === el) return; // allow the native menu while editing text
        e.preventDefault();
        e.stopPropagation();
        openContextForElement(el, e);
    });
}

function pageOf(el: El): Page {
    return state.pages.find((p) => p.elements.includes(el))!;
}

function createTextEl(
    page: Page,
    opts: Partial<Pick<TextEl, 'x' | 'y' | 'text' | 'fontSize' | 'whiteBg' | 'fontFamily' | 'bold' | 'italic' | 'color' | 'rotation' | 'opacity'>>,
): TextEl {
    const el: TextEl = {
        id: state.nextId++,
        type: 'text',
        x: opts.x ?? 60,
        y: opts.y ?? 60,
        w: 0,
        h: 0,
        text: opts.text ?? 'Text',
        fontSize: opts.fontSize ?? defaults.fontSize,
        fontFamily: opts.fontFamily ?? defaults.fontFamily,
        bold: opts.bold ?? defaults.bold,
        italic: opts.italic ?? defaults.italic,
        color: opts.color ?? defaults.color,
        whiteBg: opts.whiteBg ?? defaults.whiteBg,
        rotation: opts.rotation,
        opacity: opts.opacity,
        node: document.createElement('div'),
        dirty: true,
    };
    makeElNode(el);
    page.elements.push(el);
    page.elLayer.appendChild(el.node);
    syncElNode(el);
    return el;
}

function createShapeEl(page: Page, type: 'rect' | 'ellipse', x: number, y: number): ShapeEl {
    const el: ShapeEl = {
        id: state.nextId++,
        type,
        x,
        y,
        w: 140,
        h: 90,
        fill: defaults.fill,
        stroke: defaults.stroke,
        strokeWidth: defaults.strokeWidth,
        node: document.createElement('div'),
        dirty: true,
    };
    makeElNode(el);
    page.elements.push(el);
    page.elLayer.appendChild(el.node);
    syncElNode(el);
    return el;
}

function createRedactEl(page: Page, x: number, y: number): RedactEl {
    const el: RedactEl = {
        id: state.nextId++,
        type: 'redact',
        x,
        y,
        w: 140,
        h: 32,
        node: document.createElement('div'),
        dirty: true,
    };
    makeElNode(el);
    page.elements.push(el);
    page.elLayer.appendChild(el.node);
    syncElNode(el);
    return el;
}

/* ---- freehand pen / highlighter ----
 *
 * Unlike the other tools (click to place a default-size box, then resize),
 * this is a click-drag capture: pointerdown starts collecting page-space
 * points, pointermove appends them, pointerup finalizes into a real PathEl.
 * The bounding box is only known once the stroke is done, so points are
 * stored local to it (like every other element's x/y) — moves are free.
 */

function boxFromPoints(pts: { x: number; y: number }[], pad: number): Box {
    const minX = Math.min(...pts.map((p) => p.x)) - pad;
    const minY = Math.min(...pts.map((p) => p.y)) - pad;
    const maxX = Math.max(...pts.map((p) => p.x)) + pad;
    const maxY = Math.max(...pts.map((p) => p.y)) + pad;
    return { x: minX, y: minY, w: Math.max(maxX - minX, 1), h: Math.max(maxY - minY, 1) };
}

function pathElFrom(id: number, mode: 'pen' | 'highlighter', pts: { x: number; y: number }[]): PathEl {
    const preset = mode === 'pen' ? PEN_DEFAULTS : HIGHLIGHTER_DEFAULTS;
    const box = boxFromPoints(pts, preset.strokeWidth); // pad so round caps aren't clipped by the SVG viewBox
    return {
        id,
        type: 'path',
        x: box.x,
        y: box.y,
        w: box.w,
        h: box.h,
        points: pts.map((p) => ({ x: p.x - box.x, y: p.y - box.y })),
        mode,
        color: preset.color,
        strokeWidth: preset.strokeWidth,
        node: document.createElement('div'),
        dirty: true,
    };
}

/** A transient, uncommitted element used purely for live visual feedback while drawing. */
function makeDraftPathEl(mode: 'pen' | 'highlighter', pts: { x: number; y: number }[]): PathEl {
    const el = pathElFrom(-1, mode, pts);
    makeElNode(el);
    syncElNode(el);
    return el;
}

function updateDraftPathEl(el: PathEl, pts: { x: number; y: number }[]): void {
    const box = boxFromPoints(pts, el.strokeWidth);
    el.x = box.x;
    el.y = box.y;
    el.w = box.w;
    el.h = box.h;
    el.points = pts.map((p) => ({ x: p.x - box.x, y: p.y - box.y }));
    syncElNode(el);
}

function commitPathEl(page: Page, mode: 'pen' | 'highlighter', pts: { x: number; y: number }[]): PathEl {
    const el = pathElFrom(state.nextId++, mode, pts);
    makeElNode(el);
    page.elements.push(el);
    page.elLayer.appendChild(el.node);
    syncElNode(el);
    return el;
}

function startFreehandDraw(page: Page, mode: 'pen' | 'highlighter', downEvent: PointerEvent, node: HTMLDivElement): void {
    const rect = node.getBoundingClientRect();
    const toPagePt = (e: PointerEvent): { x: number; y: number } => ({
        x: (e.clientX - rect.left) / state.zoom,
        y: (e.clientY - rect.top) / state.zoom,
    });
    const raw: { x: number; y: number }[] = [toPagePt(downEvent)];
    try {
        node.setPointerCapture(downEvent.pointerId);
    } catch {
        /* pointer already gone — draw still works via bubbling events */
    }

    const draft = makeDraftPathEl(mode, raw);
    page.elLayer.appendChild(draft.node);

    const move = (e: PointerEvent): void => {
        const p = toPagePt(e);
        const last = raw[raw.length - 1];
        if (Math.hypot(p.x - last.x, p.y - last.y) < 1) return; // thin out near-duplicate points
        raw.push(p);
        updateDraftPathEl(draft, raw);
    };
    const up = (): void => {
        node.removeEventListener('pointermove', move);
        node.removeEventListener('pointerup', up);
        draft.node.remove();
        setPendingTool(null);
        if (raw.length < 2) return; // a tap, not a stroke — discard
        const el = commitPathEl(page, mode, raw);
        pushCommand({ kind: 'el-create', el, page });
        select(el);
    };
    node.addEventListener('pointermove', move);
    node.addEventListener('pointerup', up);
}

function createImageEl(page: Page, x: number, y: number, img: NonNullable<typeof state.pendingImage>): ImageEl {
    const maxW = Math.min(300, page.width - 80);
    const scale = Math.min(1, maxW / img.w);
    const el: ImageEl = {
        id: state.nextId++,
        type: 'image',
        x,
        y,
        w: img.w * scale,
        h: img.h * scale,
        dataUrl: img.dataUrl,
        isJpeg: img.isJpeg,
        node: document.createElement('div'),
        dirty: true,
    };
    makeElNode(el);
    page.elements.push(el);
    page.elLayer.appendChild(el.node);
    syncElNode(el);
    return el;
}

function removeEl(el: El): void {
    const page = pageOf(el);
    page.elements.splice(page.elements.indexOf(el), 1);
    // Deleted original content must still be whited out in the exported PDF.
    if (el.src) page.erasures.push(el.src);
    el.node.remove();
    if (state.selected === el) state.selected = null;
}

function deleteElWithHistory(el: El): void {
    const page = pageOf(el);
    const index = page.elements.indexOf(el);
    removeEl(el);
    pushCommand({ kind: 'el-delete', el, page, index });
}

/* ============================================================ clipboard (copy / duplicate / paste)
 *
 * A "spec" is a self-contained copy of an element's visual data — no id, no DOM
 * node, and crucially no `src` (a pasted/duplicated element is brand-new content,
 * never tied to an original PDF region), so it always exports as freshly drawn.
 */

type ElSpec =
    | { type: 'text'; w: number; h: number; text: string; fontSize: number; fontFamily: FontFamily; bold: boolean; italic: boolean; color: string; whiteBg: boolean; ocr?: boolean; rotation?: number; opacity?: number }
    | { type: 'image'; w: number; h: number; dataUrl: string; isJpeg: boolean }
    | { type: 'rect' | 'ellipse'; w: number; h: number; fill: string; stroke: string; strokeWidth: number }
    | { type: 'redact'; w: number; h: number }
    | { type: 'path'; w: number; h: number; points: { x: number; y: number }[]; mode: 'pen' | 'highlighter'; color: string; strokeWidth: number }
    | { type: 'field'; w: number; h: number; fieldType: 'text' | 'checkbox' | 'dropdown'; name: string; value: string; checked?: boolean; options?: string[]; fontSize: number };

const PASTE_OFFSET = 14; // points a duplicate/cascading paste is nudged by

let clipboard: ElSpec | null = null;
let clipboardX = 40;
let clipboardY = 40;
let pasteSeq = 0;

function specOf(el: El): ElSpec {
    if (el.type === 'text') {
        return { type: 'text', w: el.w, h: el.h, text: el.text, fontSize: el.fontSize, fontFamily: el.fontFamily, bold: el.bold, italic: el.italic, color: el.color, whiteBg: el.whiteBg, ocr: el.ocr, rotation: el.rotation, opacity: el.opacity };
    }
    if (el.type === 'image') {
        return { type: 'image', w: el.w, h: el.h, dataUrl: el.dataUrl, isJpeg: el.isJpeg };
    }
    if (el.type === 'redact') {
        return { type: 'redact', w: el.w, h: el.h };
    }
    if (el.type === 'path') {
        return { type: 'path', w: el.w, h: el.h, points: el.points.map((p) => ({ ...p })), mode: el.mode, color: el.color, strokeWidth: el.strokeWidth };
    }
    if (el.type === 'field') {
        return { type: 'field', w: el.w, h: el.h, fieldType: el.fieldType, name: el.name, value: el.value, checked: el.checked, options: el.options ? [...el.options] : undefined, fontSize: el.fontSize };
    }
    return { type: el.type, w: el.w, h: el.h, fill: el.fill, stroke: el.stroke, strokeWidth: el.strokeWidth };
}

function clampToPage(page: Page, x: number, y: number): { x: number; y: number } {
    return {
        x: Math.min(Math.max(x, 0), Math.max(0, page.width - 20)),
        y: Math.min(Math.max(y, 0), Math.max(0, page.height - 20)),
    };
}

/** Build a real element from a spec at a given position; registers it and its node. */
function buildFromSpec(page: Page, spec: ElSpec, x: number, y: number): El {
    let el: El;
    if (spec.type === 'text') {
        el = {
            id: state.nextId++, type: 'text', x, y, w: spec.w, h: spec.h,
            text: spec.text, fontSize: spec.fontSize, fontFamily: spec.fontFamily,
            bold: spec.bold, italic: spec.italic, color: spec.color, whiteBg: spec.whiteBg, ocr: spec.ocr, rotation: spec.rotation, opacity: spec.opacity,
            node: document.createElement('div'), dirty: true,
        };
    } else if (spec.type === 'image') {
        el = {
            id: state.nextId++, type: 'image', x, y, w: spec.w, h: spec.h,
            dataUrl: spec.dataUrl, isJpeg: spec.isJpeg,
            node: document.createElement('div'), dirty: true,
        };
    } else if (spec.type === 'redact') {
        el = {
            id: state.nextId++, type: 'redact', x, y, w: spec.w, h: spec.h,
            node: document.createElement('div'), dirty: true,
        };
    } else if (spec.type === 'path') {
        el = {
            id: state.nextId++, type: 'path', x, y, w: spec.w, h: spec.h,
            points: spec.points.map((p) => ({ ...p })), mode: spec.mode, color: spec.color, strokeWidth: spec.strokeWidth,
            node: document.createElement('div'), dirty: true,
        };
    } else if (spec.type === 'field') {
        el = {
            id: state.nextId++, type: 'field', x, y, w: spec.w, h: spec.h,
            fieldType: spec.fieldType, name: spec.name, value: spec.value, checked: spec.checked,
            options: spec.options ? [...spec.options] : undefined, fontSize: spec.fontSize,
            node: document.createElement('div'), dirty: true,
        };
    } else {
        el = {
            id: state.nextId++, type: spec.type, x, y, w: spec.w, h: spec.h,
            fill: spec.fill, stroke: spec.stroke, strokeWidth: spec.strokeWidth,
            node: document.createElement('div'), dirty: true,
        };
    }
    makeElNode(el);
    page.elements.push(el);
    page.elLayer.appendChild(el.node);
    syncElNode(el);
    return el;
}

function copyEl(el: El): void {
    clipboard = specOf(el);
    clipboardX = el.x;
    clipboardY = el.y;
    pasteSeq = 0;
}

function duplicateEl(el: El): void {
    const page = pageOf(el);
    const spec = specOf(el);
    const { x, y } = clampToPage(page, el.x + PASTE_OFFSET, el.y + PASTE_OFFSET);
    const copy = buildFromSpec(page, spec, x, y);
    pushCommand({ kind: 'el-create', el: copy, page });
    select(copy);
}

/** Paste the clipboard onto a page at an explicit point (e.g. the right-click location). */
function pasteAt(page: Page, x: number, y: number): void {
    if (!clipboard) return;
    const pos = clampToPage(page, x, y);
    const el = buildFromSpec(page, clipboard, pos.x, pos.y);
    pushCommand({ kind: 'el-create', el, page });
    select(el);
}

/** Paste with no cursor (keyboard): cascade down-right from the copied element. */
function pasteCascade(): void {
    if (!clipboard) return;
    const page = (state.selected && pageOf(state.selected)) || state.pages[0];
    if (!page) return;
    pasteSeq += 1;
    pasteAt(page, clipboardX + PASTE_OFFSET * pasteSeq, clipboardY + PASTE_OFFSET * pasteSeq);
}

/* ============================================================ context menu */

interface CtxState {
    target: El | null; // element under the cursor, or null on empty page
    page: Page | null;
    x: number; // page-local coords, for paste
    y: number;
}
const ctx: CtxState = { target: null, page: null, x: 0, y: 0 };
let ctxMenu: HTMLDivElement | null = null;

function pageLocalPoint(page: Page, clientX: number, clientY: number): { x: number; y: number } {
    const rect = page.node.getBoundingClientRect();
    return { x: (clientX - rect.left) / state.zoom, y: (clientY - rect.top) / state.zoom };
}

function ensureContextMenu(): HTMLDivElement {
    if (ctxMenu) return ctxMenu;
    const menu = document.createElement('div');
    menu.id = 'pel-context-menu';
    menu.className = 'pel-ctx-menu';
    menu.innerHTML =
        `<button data-act="copy">Copy</button>` +
        `<button data-act="duplicate">Duplicate</button>` +
        `<button data-act="paste">Paste</button>` +
        `<div class="pel-ctx-sep"></div>` +
        `<button data-act="delete" class="pel-ctx-danger">Delete</button>`;

    menu.addEventListener('contextmenu', (e) => e.preventDefault());
    menu.addEventListener('click', (e) => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-act]');
        if (!btn || btn.disabled) return;
        runContextAction(btn.dataset.act as string);
        hideContextMenu();
    });

    // Dismiss on any press outside the menu, on scroll, or on resize.
    document.addEventListener('pointerdown', (e) => {
        if (ctxMenu?.classList.contains('open') && !ctxMenu.contains(e.target as Node)) hideContextMenu();
    }, true);
    window.addEventListener('resize', hideContextMenu);
    document.addEventListener('scroll', hideContextMenu, true);

    document.body.appendChild(menu);
    ctxMenu = menu;
    return menu;
}

function runContextAction(act: string): void {
    switch (act) {
        case 'copy':
            if (ctx.target) copyEl(ctx.target);
            break;
        case 'duplicate':
            if (ctx.target) duplicateEl(ctx.target);
            break;
        case 'paste':
            if (ctx.page) pasteAt(ctx.page, ctx.x, ctx.y);
            break;
        case 'delete':
            if (ctx.target) deleteElWithHistory(ctx.target);
            break;
    }
}

function showContextMenu(clientX: number, clientY: number): void {
    const menu = ensureContextMenu();
    const hasTarget = ctx.target !== null;
    menu.querySelector<HTMLButtonElement>('[data-act="copy"]')!.disabled = !hasTarget;
    menu.querySelector<HTMLButtonElement>('[data-act="duplicate"]')!.disabled = !hasTarget;
    menu.querySelector<HTMLButtonElement>('[data-act="delete"]')!.disabled = !hasTarget;
    menu.querySelector<HTMLButtonElement>('[data-act="paste"]')!.disabled = clipboard === null;

    menu.classList.add('open');
    // Clamp within the viewport now that it has a measurable size.
    const { width, height } = menu.getBoundingClientRect();
    const x = Math.min(clientX, window.innerWidth - width - 6);
    const y = Math.min(clientY, window.innerHeight - height - 6);
    menu.style.left = `${Math.max(6, x)}px`;
    menu.style.top = `${Math.max(6, y)}px`;
}

function hideContextMenu(): void {
    ctxMenu?.classList.remove('open');
}

/** Right-click on an element. */
function openContextForElement(el: El, e: MouseEvent): void {
    const page = pageOf(el);
    ctx.target = el;
    ctx.page = page;
    Object.assign(ctx, pageLocalPoint(page, e.clientX, e.clientY));
    select(el);
    showContextMenu(e.clientX, e.clientY);
}

/** Right-click on an empty area of a page. */
function openContextForPage(page: Page, e: MouseEvent): void {
    ctx.target = null;
    ctx.page = page;
    Object.assign(ctx, pageLocalPoint(page, e.clientX, e.clientY));
    select(null);
    showContextMenu(e.clientX, e.clientY);
}

/* ============================================================ selection / editing */

function select(el: El | null): void {
    if (state.editing && state.editing !== el) stopEditing();
    if (editingField && editingField.el !== el) stopEditingField(editingField.el);
    if (state.selected && state.selected !== el) {
        state.selected.node.classList.remove('selected');
        state.selected.node.querySelectorAll('.h').forEach((h) => h.remove());
    }
    state.selected = el;
    if (el) {
        el.node.classList.add('selected');
        // Freehand strokes can be moved but not resized in this version.
        if (el.type !== 'text' && el.type !== 'path') addHandles(el);
        reflectSelectionInToolbar(el);
    }
}

function addHandles(el: El): void {
    for (const dir of ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']) {
        const h = document.createElement('div');
        h.className = 'h';
        h.dataset.dir = dir;
        h.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            e.preventDefault();
            startResize(el, dir, e);
        });
        el.node.appendChild(h);
    }
}

let editSnapshot: Snap | null = null;

function startEditing(el: TextEl): void {
    if (state.editing === el) return;
    select(el);
    state.editing = el;
    editSnapshot = snapshotEl(el);
    el.node.classList.add('editing');
    const content = el.node.querySelector<HTMLDivElement>('.pel-content')!;
    content.contentEditable = 'true';
    content.focus();
    const range = document.createRange();
    range.selectNodeContents(content);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    content.addEventListener('blur', stopEditing, { once: true });
}

function stopEditing(): void {
    const el = state.editing;
    if (!el) return;
    state.editing = null;
    el.node.classList.remove('editing');
    const content = el.node.querySelector<HTMLDivElement>('.pel-content')!;
    content.contentEditable = 'false';
    const newText = content.innerText.replace(/\n$/, '');
    if (newText !== el.text) {
        el.text = newText;
        markDirty(el);
    }
    const before = editSnapshot;
    editSnapshot = null;
    if (!el.text.trim()) {
        deleteElWithHistory(el);
        return;
    }
    syncElNode(el);
    if (before) pushElChange(el, before);
}

let editingField: { el: FieldEl; before: Snap } | null = null;

/** Activates a detected form field's control for typing/checking/choosing. */
function startEditingField(el: FieldEl): void {
    if (editingField?.el === el) return;
    select(el);
    editingField = { el, before: snapshotEl(el) };
    el.node.classList.add('editing');
    const control = el.node.querySelector<HTMLInputElement | HTMLSelectElement>('.pel-field-control')!;
    control.style.pointerEvents = 'auto';
    control.focus();
    if (control instanceof HTMLInputElement && control.type === 'text') control.select();
}

function stopEditingField(el: FieldEl): void {
    if (editingField?.el !== el) return;
    const before = editingField.before;
    editingField = null;
    el.node.classList.remove('editing');
    const control = el.node.querySelector<HTMLElement>('.pel-field-control');
    if (control) control.style.pointerEvents = 'none';
    pushElChange(el, before);
}

/* ============================================================ snapping & alignment guides
 *
 * Editor-only polish: while dragging or resizing, the moved edge(s) snap to the
 * page center, a fixed margin inset, or another element's edges/center on the
 * same page, with a magenta guide line shown for as long as the snap holds.
 * Nothing here touches the data model, history, or export.
 */

const SNAP_PX = 6; // screen px within which an edge snaps
const MARGIN_PT = 36; // page margin inset used as a snap target, in PDF points (0.5in)

function computeSnapTargets(page: Page, exclude: El): { v: number[]; h: number[] } {
    const v = [MARGIN_PT, page.width / 2, page.width - MARGIN_PT];
    const h = [MARGIN_PT, page.height / 2, page.height - MARGIN_PT];
    for (const other of page.elements) {
        if (other === exclude) continue;
        v.push(other.x, other.x + other.w / 2, other.x + other.w);
        h.push(other.y, other.y + other.h / 2, other.y + other.h);
    }
    return { v, h };
}

/** Nearest target within threshold, or null. Page-space in, page-space out. */
function nearestSnap(value: number, targets: number[], threshold: number): number | null {
    let best: number | null = null;
    let bestDist = threshold;
    for (const t of targets) {
        const d = Math.abs(value - t);
        if (d < bestDist) {
            bestDist = d;
            best = t;
        }
    }
    return best;
}

let guideV: HTMLDivElement | null = null;
let guideH: HTMLDivElement | null = null;

function ensureGuides(): { v: HTMLDivElement; h: HTMLDivElement } {
    if (!guideV) {
        guideV = document.createElement('div');
        guideV.className = 'snap-guide-v';
        document.body.appendChild(guideV);
    }
    if (!guideH) {
        guideH = document.createElement('div');
        guideH.className = 'snap-guide-h';
        document.body.appendChild(guideH);
    }
    return { v: guideV, h: guideH };
}

/** pageX is in page-space points; converted to a fixed-position line over the page's current screen rect. */
function showGuideV(page: Page, pageX: number): void {
    const { v } = ensureGuides();
    const rect = page.node.getBoundingClientRect();
    v.style.left = `${rect.left + pageX * state.zoom}px`;
    v.style.top = `${rect.top}px`;
    v.style.height = `${rect.height}px`;
    v.classList.add('visible');
}

function showGuideH(page: Page, pageY: number): void {
    const { h } = ensureGuides();
    const rect = page.node.getBoundingClientRect();
    h.style.top = `${rect.top + pageY * state.zoom}px`;
    h.style.left = `${rect.left}px`;
    h.style.width = `${rect.width}px`;
    h.classList.add('visible');
}

function hideGuides(): void {
    guideV?.classList.remove('visible');
    guideH?.classList.remove('visible');
}

/* ============================================================ drag & resize */

function startDrag(el: El, downEvent: PointerEvent): void {
    const startX = el.x;
    const startY = el.y;
    const before = snapshotEl(el);
    const page = pageOf(el);
    const px = downEvent.clientX;
    const py = downEvent.clientY;
    try {
        el.node.setPointerCapture(downEvent.pointerId);
    } catch {
        /* pointer already gone — drag still works via bubbling events */
    }

    const move = (e: PointerEvent): void => {
        let nx = Math.min(Math.max(startX + (e.clientX - px) / state.zoom, -el.w / 2), page.width - el.w / 2);
        let ny = Math.min(Math.max(startY + (e.clientY - py) / state.zoom, -el.h / 2), page.height - el.h / 2);

        const threshold = SNAP_PX / state.zoom;
        const { v: vTargets, h: hTargets } = computeSnapTargets(page, el);
        let snappedV = false;
        for (const [edge, offset] of [[nx, 0], [nx + el.w / 2, el.w / 2], [nx + el.w, el.w]] as const) {
            const t = nearestSnap(edge, vTargets, threshold);
            if (t !== null) {
                nx = t - offset;
                showGuideV(page, t);
                snappedV = true;
                break;
            }
        }
        if (!snappedV) guideV?.classList.remove('visible');
        let snappedH = false;
        for (const [edge, offset] of [[ny, 0], [ny + el.h / 2, el.h / 2], [ny + el.h, el.h]] as const) {
            const t = nearestSnap(edge, hTargets, threshold);
            if (t !== null) {
                ny = t - offset;
                showGuideH(page, t);
                snappedH = true;
                break;
            }
        }
        if (!snappedH) guideH?.classList.remove('visible');

        el.x = nx;
        el.y = ny;
        if (el.x !== startX || el.y !== startY) markDirty(el);
        syncElNode(el);
    };
    const up = (): void => {
        el.node.removeEventListener('pointermove', move);
        el.node.removeEventListener('pointerup', up);
        hideGuides();
        pushElChange(el, before);
    };
    el.node.addEventListener('pointermove', move);
    el.node.addEventListener('pointerup', up);
}

function startResize(el: El, dir: string, downEvent: PointerEvent): void {
    const start = { x: el.x, y: el.y, w: el.w, h: el.h };
    const before = snapshotEl(el);
    const page = pageOf(el);
    const px = downEvent.clientX;
    const py = downEvent.clientY;
    const handle = downEvent.target as HTMLElement;
    try {
        handle.setPointerCapture(downEvent.pointerId);
    } catch {
        /* pointer already gone — resize still works via bubbling events */
    }
    const keepAspect = el.type === 'image' && dir.length === 2;
    const aspect = start.w / start.h;

    const move = (e: PointerEvent): void => {
        const dx = (e.clientX - px) / state.zoom;
        const dy = (e.clientY - py) / state.zoom;
        let { x, y, w, h } = start;
        if (dir.includes('e')) w += dx;
        if (dir.includes('w')) { w -= dx; x += dx; }
        if (dir.includes('s')) h += dy;
        if (dir.includes('n')) { h -= dy; y += dy; }
        if (keepAspect) {
            h = w / aspect;
            if (dir.includes('n')) y = start.y + start.h - h;
        } else {
            // Snap only the edge(s) actually being dragged. Skipped for aspect-locked
            // (image) resizes since snapping one axis would fight the locked ratio.
            const threshold = SNAP_PX / state.zoom;
            const { v: vTargets, h: hTargets } = computeSnapTargets(page, el);
            let snappedV = false;
            if (dir.includes('e')) {
                const t = nearestSnap(x + w, vTargets, threshold);
                if (t !== null) { w = t - x; showGuideV(page, t); snappedV = true; }
            } else if (dir.includes('w')) {
                const t = nearestSnap(x, vTargets, threshold);
                if (t !== null) { w += x - t; x = t; showGuideV(page, t); snappedV = true; }
            }
            if (!snappedV) guideV?.classList.remove('visible');
            let snappedH = false;
            if (dir.includes('s')) {
                const t = nearestSnap(y + h, hTargets, threshold);
                if (t !== null) { h = t - y; showGuideH(page, t); snappedH = true; }
            } else if (dir.includes('n')) {
                const t = nearestSnap(y, hTargets, threshold);
                if (t !== null) { h += y - t; y = t; showGuideH(page, t); snappedH = true; }
            }
            if (!snappedH) guideH?.classList.remove('visible');
        }
        if (w < 8 || h < 8) return;
        el.x = x; el.y = y; el.w = w; el.h = h;
        markDirty(el);
        syncElNode(el);
    };
    const up = (): void => {
        handle.removeEventListener('pointermove', move);
        handle.removeEventListener('pointerup', up);
        hideGuides();
        pushElChange(el, before);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
}

/* ============================================================ toolbar */

let fontFamilySel: HTMLSelectElement;
let fontSizeInput: HTMLInputElement;
let boldBtn: HTMLButtonElement;
let italicBtn: HTMLButtonElement;
let textColorInput: HTMLInputElement;
let textBgBtn: HTMLButtonElement;
let fillColorInput: HTMLInputElement;
let strokeColorInput: HTMLInputElement;
let strokeWidthInput: HTMLInputElement;

function reflectSelectionInToolbar(el: El): void {
    if (el.type === 'text') {
        fontFamilySel.value = el.fontFamily;
        fontSizeInput.value = String(Math.round(el.fontSize));
        boldBtn.classList.toggle('active', el.bold);
        italicBtn.classList.toggle('active', el.italic);
        textColorInput.value = el.color;
        textBgBtn.classList.toggle('active', el.whiteBg);
    } else if (el.type === 'rect' || el.type === 'ellipse') {
        fillColorInput.value = el.fill;
        strokeColorInput.value = el.stroke;
        strokeWidthInput.value = String(el.strokeWidth);
    }
}

function applyTextProp<K extends keyof TextEl & keyof typeof defaults>(key: K, value: TextEl[K] & (typeof defaults)[K]): void {
    (defaults as Record<string, unknown>)[key] = value;
    if (state.selected?.type === 'text') {
        const before = snapshotEl(state.selected);
        state.selected[key] = value;
        markDirty(state.selected);
        syncElNode(state.selected);
        pushElChange(state.selected, before);
    }
}

function applyShapeProp<K extends 'fill' | 'stroke' | 'strokeWidth'>(key: K, value: ShapeEl[K]): void {
    (defaults as Record<string, unknown>)[key] = value;
    if (state.selected && (state.selected.type === 'rect' || state.selected.type === 'ellipse')) {
        const before = snapshotEl(state.selected);
        state.selected[key] = value;
        markDirty(state.selected);
        syncElNode(state.selected);
        pushElChange(state.selected, before);
    }
}

/* ---- insert tools ---- */

let toolButtons: NodeListOf<HTMLButtonElement>;

function setPendingTool(tool: Tool | null): void {
    state.pendingTool = tool;
    toolButtons.forEach((b) => b.classList.toggle('active', b.dataset.tool === tool));
    document.querySelectorAll('.pdf-page').forEach((p) => p.classList.toggle('placing', tool !== null));
    const hint =
        tool === 'pen' || tool === 'highlighter'
            ? 'Click and drag on a page to draw. Press Esc to cancel.'
            : tool
              ? `Click anywhere on a page to place the ${tool}. Press Esc to cancel.`
              : null;
    setHint(hint);
}

function placePendingTool(page: Page, x: number, y: number): void {
    const tool = state.pendingTool;
    if (!tool) return;
    let el: El;
    if (tool === 'text') {
        el = createTextEl(page, { x, y });
        pushCommand({ kind: 'el-create', el, page });
        setPendingTool(null);
        select(el);
        startEditing(el);
        return;
    } else if (tool === 'image' && state.pendingImage) {
        el = createImageEl(page, x, y, state.pendingImage);
        state.pendingImage = null;
    } else if (tool === 'rect' || tool === 'ellipse') {
        el = createShapeEl(page, tool, x, y);
    } else if (tool === 'redact') {
        el = createRedactEl(page, x, y);
    } else {
        setPendingTool(null);
        return;
    }
    pushCommand({ kind: 'el-create', el, page });
    setPendingTool(null);
    select(el);
}

/* ---- image loading ---- */

let imgFileInput: HTMLInputElement;

async function handleImageFileChange(): Promise<void> {
    const file = imgFileInput.files?.[0];
    if (!file) return;
    const dataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = () => reject(r.error);
        r.readAsDataURL(file);
    });
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Could not load image'));
        img.src = dataUrl;
    });

    let finalUrl = dataUrl;
    let isJpeg = file.type === 'image/jpeg';
    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        c.getContext('2d')!.drawImage(img, 0, 0);
        finalUrl = c.toDataURL('image/png');
        isJpeg = false;
    }
    state.pendingImage = { dataUrl: finalUrl, isJpeg, w: img.naturalWidth, h: img.naturalHeight };
    setPendingTool('image');
}

/* ============================================================ OCR (make a scanned page searchable)
 *
 * Runs Tesseract.js on a page's rendered background canvas and turns each
 * recognized line into a text element positioned over it — the exact same
 * TextEl/export pipeline as real extracted PDF text, marked `ocr: true` so it
 * draws at 0 opacity on export: present (searchable/selectable in any PDF
 * viewer) but invisible, since the scanned image underneath is what's seen.
 * Because OCR'd elements have no `src` (they aren't tied to an original PDF
 * region), they never force the page to flatten — the scan stays pixel-exact.
 */

let ocrWorkerPromise: Promise<TesseractWorker> | null = null;

function getOcrWorker(): Promise<TesseractWorker> {
    if (!ocrWorkerPromise) ocrWorkerPromise = createWorker('eng');
    return ocrWorkerPromise;
}

async function ocrPage(page: Page, button: HTMLButtonElement): Promise<void> {
    if (button.disabled) return;
    button.disabled = true;
    const originalLabel = button.textContent;
    button.textContent = 'Reading page…';
    try {
        const worker = await getOcrWorker();
        const { data } = await worker.recognize(page.bgCanvas, {}, { blocks: true });

        const lines: { text: string; x0: number; y0: number; x1: number; y1: number }[] = [];
        for (const block of data.blocks ?? []) {
            for (const para of block.paragraphs) {
                for (const line of para.lines) {
                    if (line.text.trim()) lines.push({ text: line.text.trim(), ...line.bbox });
                }
            }
        }

        if (lines.length === 0) {
            showToast('No text detected on this page');
            return;
        }

        for (const line of lines) {
            const x = line.x0 / RENDER_SCALE;
            const y = line.y0 / RENDER_SCALE;
            const h = (line.y1 - line.y0) / RENDER_SCALE;
            const fontSize = Math.max(h * 0.85, 6);
            const el = createTextEl(page, { x, y: y - (fontSize - h) / 2, text: line.text, fontSize });
            el.ocr = true;
            syncElNode(el);
            pushCommand({ kind: 'el-create', el, page });
        }
        showToast(`Made ${lines.length} line${lines.length === 1 ? '' : 's'} of text searchable`);
    } catch (err) {
        console.error('OCR failed', err);
        showToast('OCR failed — see console for details');
    } finally {
        button.disabled = false;
        button.textContent = originalLabel;
    }
}

/* ============================================================ signature tool
 *
 * A signature is just an inserted image — drawn strokes (or typed text in a
 * script font) are rendered to an offscreen canvas, trimmed to their ink
 * bounding box, and handed to the exact same pendingImage → 'image' placement
 * flow as the Image tool, so it's draggable/resizable/undoable for free.
 */

const SIG_LAST_KEY = 'pdf-studio-last-signature';
const SIG_FONTS = ['"Segoe Script", cursive', '"Brush Script MT", cursive', 'cursive'];

let sigStrokes: { x: number; y: number }[][] = [];
let sigDrawing = false;
let sigMode: 'draw' | 'type' = 'draw';
let sigFontIndex = 0;

function sigModal(): HTMLDivElement {
    return $<HTMLDivElement>('#sig-modal');
}

function openSignatureModal(): void {
    sigModal().classList.remove('hidden');
    setSigMode('draw');
    clearSigCanvas();
    const lastBtn = $<HTMLButtonElement>('#sig-use-last');
    lastBtn.classList.toggle('hidden', !localStorage.getItem(SIG_LAST_KEY));
}

function closeSignatureModal(): void {
    sigModal().classList.add('hidden');
}

function setSigMode(mode: 'draw' | 'type'): void {
    sigMode = mode;
    $('#sig-tab-draw').classList.toggle('active', mode === 'draw');
    $('#sig-tab-type').classList.toggle('active', mode === 'type');
    $('#sig-pane-draw').classList.toggle('hidden', mode !== 'draw');
    $('#sig-pane-type').classList.toggle('hidden', mode !== 'type');
}

function clearSigCanvas(): void {
    sigStrokes = [];
    const canvas = $<HTMLCanvasElement>('#sig-canvas');
    canvas.getContext('2d')!.clearRect(0, 0, canvas.width, canvas.height);
}

function redrawSigCanvas(): void {
    const canvas = $<HTMLCanvasElement>('#sig-canvas');
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const stroke of sigStrokes) {
        if (stroke.length < 2) continue;
        ctx.beginPath();
        ctx.moveTo(stroke[0].x, stroke[0].y);
        for (let i = 1; i < stroke.length; i++) ctx.lineTo(stroke[i].x, stroke[i].y);
        ctx.stroke();
    }
}

function buildSigFontOptions(): void {
    const host = $<HTMLDivElement>('#sig-font-options');
    host.innerHTML = '';
    SIG_FONTS.forEach((font, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sig-font-btn';
        btn.style.fontFamily = font;
        btn.textContent = 'Signature';
        btn.classList.toggle('active', i === sigFontIndex);
        btn.addEventListener('click', () => {
            sigFontIndex = i;
            host.querySelectorAll('.sig-font-btn').forEach((b, bi) => b.classList.toggle('active', bi === i));
            renderTypedSigPreview();
        });
        host.appendChild(btn);
    });
}

function renderTypedSigPreview(): void {
    const btn = $<HTMLDivElement>('#sig-font-options').children[sigFontIndex] as HTMLElement | undefined;
    if (!btn) return;
    const text = $<HTMLInputElement>('#sig-text').value.trim();
    btn.textContent = text || 'Signature';
}

/** Draw strokes to a PNG, trimmed to the ink's bounding box (with a small margin). */
function drawnSignatureToDataUrl(): { dataUrl: string; w: number; h: number } | null {
    const pts = sigStrokes.flat();
    if (pts.length === 0) return null;
    const margin = 8;
    const minX = Math.max(0, Math.min(...pts.map((p) => p.x)) - margin);
    const minY = Math.max(0, Math.min(...pts.map((p) => p.y)) - margin);
    const maxX = Math.max(...pts.map((p) => p.x)) + margin;
    const maxY = Math.max(...pts.map((p) => p.y)) + margin;
    const w = Math.max(maxX - minX, 10);
    const h = Math.max(maxY - minY, 10);

    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    const octx = out.getContext('2d')!;
    octx.strokeStyle = '#111';
    octx.lineWidth = 2.5;
    octx.lineCap = 'round';
    octx.lineJoin = 'round';
    for (const stroke of sigStrokes) {
        if (stroke.length < 2) continue;
        octx.beginPath();
        octx.moveTo(stroke[0].x - minX, stroke[0].y - minY);
        for (let i = 1; i < stroke.length; i++) octx.lineTo(stroke[i].x - minX, stroke[i].y - minY);
        octx.stroke();
    }
    return { dataUrl: out.toDataURL('image/png'), w, h };
}

/** Render typed text in the chosen script font to a transparent PNG, sized to fit. */
function typedSignatureToDataUrl(): { dataUrl: string; w: number; h: number } | null {
    const text = $<HTMLInputElement>('#sig-text').value.trim();
    if (!text) return null;
    const font = SIG_FONTS[sigFontIndex];
    const fontSize = 64;

    const measure = document.createElement('canvas').getContext('2d')!;
    measure.font = `${fontSize}px ${font}`;
    const w = Math.max(Math.ceil(measure.measureText(text).width) + 24, 40);
    const h = Math.ceil(fontSize * 1.6);

    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    const octx = out.getContext('2d')!;
    octx.font = `${fontSize}px ${font}`;
    octx.fillStyle = '#111';
    octx.textBaseline = 'alphabetic';
    octx.fillText(text, 12, fontSize * 1.05);
    return { dataUrl: out.toDataURL('image/png'), w, h };
}

function confirmSignature(): void {
    const result = sigMode === 'draw' ? drawnSignatureToDataUrl() : typedSignatureToDataUrl();
    if (!result) {
        showToast(sigMode === 'draw' ? 'Draw a signature first' : 'Type a name first');
        return;
    }
    localStorage.setItem(SIG_LAST_KEY, JSON.stringify(result));
    insertSignature(result);
}

function insertSignature(sig: { dataUrl: string; w: number; h: number }): void {
    state.pendingImage = { dataUrl: sig.dataUrl, isJpeg: false, w: sig.w, h: sig.h };
    closeSignatureModal();
    setPendingTool('image');
    setHint('Click anywhere on a page to place the signature.');
}

function useLastSignature(): void {
    const raw = localStorage.getItem(SIG_LAST_KEY);
    if (!raw) return;
    try {
        insertSignature(JSON.parse(raw));
    } catch {
        localStorage.removeItem(SIG_LAST_KEY);
    }
}

function sigPointerPos(canvas: HTMLCanvasElement, e: PointerEvent): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    return {
        x: ((e.clientX - rect.left) / rect.width) * canvas.width,
        y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
}

/* ============================================================ stamps: watermark / page numbers / header-footer
 *
 * All three just insert plain TextEls via createTextEl() + the usual
 * el-create history command — no new element type, no export changes. Once
 * placed, a stamp is exactly as editable/movable/deletable/undoable as any
 * text the user typed by hand.
 */

const STAMP_MARGIN = 24; // points, inset from the page edge for page numbers/headers/footers

function setStampTab(tab: 'watermark' | 'pagenum' | 'headerfooter'): void {
    $('#stamp-tab-watermark').classList.toggle('active', tab === 'watermark');
    $('#stamp-tab-pagenum').classList.toggle('active', tab === 'pagenum');
    $('#stamp-tab-headerfooter').classList.toggle('active', tab === 'headerfooter');
    $('#stamp-pane-watermark').classList.toggle('hidden', tab !== 'watermark');
    $('#stamp-pane-pagenum').classList.toggle('hidden', tab !== 'pagenum');
    $('#stamp-pane-headerfooter').classList.toggle('hidden', tab !== 'headerfooter');
}

function openStampModal(): void {
    $<HTMLDivElement>('#stamp-modal').classList.remove('hidden');
    setStampTab('watermark');
}

function closeStampModal(): void {
    $<HTMLDivElement>('#stamp-modal').classList.add('hidden');
}

function applyWatermark(): void {
    const text = $<HTMLInputElement>('#wm-text').value.trim();
    if (!text) {
        showToast('Enter watermark text');
        return;
    }
    const fontSize = Math.min(Math.max(Number($<HTMLInputElement>('#wm-size').value) || 48, 6), 200);
    const color = $<HTMLInputElement>('#wm-color').value;
    const opacity = Math.min(Math.max(Number($<HTMLInputElement>('#wm-opacity').value) || 30, 1), 100) / 100;
    const rotation = Number($<HTMLInputElement>('#wm-rotation').value) || 0;
    const scope = $<HTMLSelectElement>('#wm-scope').value;
    const targets = scope === 'current' ? [state.pages[currentPageIndex()]] : state.pages;

    for (const page of targets) {
        if (!page) continue;
        const el = createTextEl(page, { text, fontSize, color, opacity, rotation, x: 0, y: 0 });
        // createTextEl doesn't know the rendered size up front (it's measured in
        // syncElNode after the DOM node exists) — center it now that el.w/h are known.
        el.x = page.width / 2 - el.w / 2;
        el.y = page.height / 2 - el.h / 2;
        syncElNode(el);
        pushCommand({ kind: 'el-create', el, page });
    }
    scheduleStripRefresh();
    closeStampModal();
    showToast(`Watermark added to ${targets.length} page${targets.length === 1 ? '' : 's'}`);
}

function applyPageNumbers(): void {
    const format = $<HTMLInputElement>('#pn-format').value.trim() || 'Page {n} of {N}';
    const corner = $<HTMLSelectElement>('#pn-corner').value; // 'br' | 'bl' | 'tr' | 'tl'
    const fontSize = Math.min(Math.max(Number($<HTMLInputElement>('#pn-size').value) || 10, 6), 48);
    const total = state.pages.length;

    state.pages.forEach((page, i) => {
        const text = format.replace(/\{n\}/g, String(i + 1)).replace(/\{N\}/g, String(total));
        const el = createTextEl(page, { text, fontSize, x: 0, y: 0 });
        if (corner === 'br') { el.x = page.width - STAMP_MARGIN - el.w; el.y = page.height - STAMP_MARGIN - el.h; }
        else if (corner === 'bl') { el.x = STAMP_MARGIN; el.y = page.height - STAMP_MARGIN - el.h; }
        else if (corner === 'tr') { el.x = page.width - STAMP_MARGIN - el.w; el.y = STAMP_MARGIN; }
        else { el.x = STAMP_MARGIN; el.y = STAMP_MARGIN; }
        syncElNode(el);
        pushCommand({ kind: 'el-create', el, page });
    });
    scheduleStripRefresh();
    closeStampModal();
    showToast(`Page numbers added to ${total} page${total === 1 ? '' : 's'} — this is a one-time stamp, not a live field`);
}

function applyHeaderFooter(): void {
    const fontSize = Math.min(Math.max(Number($<HTMLInputElement>('#hf-size').value) || 10, 6), 48);
    const fields: { sel: string; corner: 'tl' | 'tc' | 'tr' | 'bl' | 'bc' | 'br' }[] = [
        { sel: '#hf-header-left', corner: 'tl' },
        { sel: '#hf-header-center', corner: 'tc' },
        { sel: '#hf-header-right', corner: 'tr' },
        { sel: '#hf-footer-left', corner: 'bl' },
        { sel: '#hf-footer-center', corner: 'bc' },
        { sel: '#hf-footer-right', corner: 'br' },
    ];
    const values = fields
        .map((f) => ({ ...f, text: $<HTMLInputElement>(f.sel).value.trim() }))
        .filter((f) => f.text.length > 0);
    if (values.length === 0) {
        showToast('Enter at least one header or footer field');
        return;
    }

    let count = 0;
    for (const page of state.pages) {
        for (const f of values) {
            const el = createTextEl(page, { text: f.text, fontSize, x: 0, y: 0 });
            const top = f.corner.startsWith('t');
            el.y = top ? STAMP_MARGIN : page.height - STAMP_MARGIN - el.h;
            if (f.corner.endsWith('c')) el.x = page.width / 2 - el.w / 2;
            else if (f.corner.endsWith('r')) el.x = page.width - STAMP_MARGIN - el.w;
            else el.x = STAMP_MARGIN;
            syncElNode(el);
            pushCommand({ kind: 'el-create', el, page });
            count++;
        }
    }
    scheduleStripRefresh();
    closeStampModal();
    showToast(`Added ${count} header/footer element${count === 1 ? '' : 's'}`);
}

/* ============================================================ page thumbnails (left rail)
 *
 * A small canvas per page, composited from the erased background render plus the
 * live overlay elements, so the strip mirrors the document (including edits).
 * Clicking a thumbnail scrolls the workspace to that page.
 */

const THUMB_W = 128; // css px

function renderThumb(page: Page, canvas: HTMLCanvasElement): void {
    const dpr = window.devicePixelRatio || 1;
    const s = THUMB_W / page.width; // page points → thumb css px
    const wCss = THUMB_W;
    const hCss = page.height * s;
    canvas.width = Math.round(wCss * dpr);
    canvas.height = Math.round(hCss * dpr);
    canvas.style.width = `${wCss}px`;
    canvas.style.height = `${hCss}px`;

    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, wCss, hCss);
    if (page.bgCanvas.width > 0) ctx.drawImage(page.bgCanvas, 0, 0, wCss, hCss);

    for (const el of page.elements) {
        const x = el.x * s;
        const y = el.y * s;
        if (el.type === 'text') {
            if (el.whiteBg) {
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(x, y, el.w * s, el.h * s);
            }
            const fs = Math.max(el.fontSize * s, 1);
            ctx.font = `${el.italic ? 'italic ' : ''}${el.bold ? '700' : '400'} ${fs}px ${CSS_FONT[el.fontFamily]}`;
            ctx.fillStyle = el.color;
            ctx.textBaseline = 'alphabetic';
            let ly = y + fs * 0.9;
            for (const line of el.text.split('\n')) {
                ctx.fillText(line, x + TEXT_PAD * s, ly);
                ly += fs * 1.2;
            }
        } else if (el.type === 'image') {
            const img = el.node.querySelector('img');
            if (img && img.complete && img.naturalWidth) {
                try { ctx.drawImage(img, x, y, el.w * s, el.h * s); } catch { /* not ready */ }
            }
        } else if (el.type === 'redact') {
            ctx.fillStyle = '#000000';
            ctx.fillRect(x, y, el.w * s, el.h * s);
        } else if (el.type === 'path') {
            const pts = el.points;
            if (pts.length > 0) {
                ctx.save();
                ctx.strokeStyle = el.color;
                ctx.lineWidth = Math.max(el.strokeWidth * s, 0.5);
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                if (el.mode === 'highlighter') ctx.globalAlpha = HIGHLIGHTER_DEFAULTS.opacity;
                ctx.beginPath();
                ctx.moveTo(x + pts[0].x * s, y + pts[0].y * s);
                for (let i = 1; i < pts.length - 1; i++) {
                    const mx = x + ((pts[i].x + pts[i + 1].x) / 2) * s;
                    const my = y + ((pts[i].y + pts[i + 1].y) / 2) * s;
                    ctx.quadraticCurveTo(x + pts[i].x * s, y + pts[i].y * s, mx, my);
                }
                const last = pts[pts.length - 1];
                ctx.lineTo(x + last.x * s, y + last.y * s);
                ctx.stroke();
                ctx.restore();
            }
        } else if (el.type === 'field') {
            ctx.strokeStyle = 'rgba(90, 90, 90, 0.6)';
            ctx.lineWidth = Math.max(s, 0.5);
            ctx.strokeRect(x, y, el.w * s, el.h * s);
            if (el.fieldType === 'checkbox') {
                if (el.checked) {
                    ctx.strokeStyle = '#111';
                    ctx.lineWidth = Math.max(1.5 * s, 0.7);
                    ctx.beginPath();
                    ctx.moveTo(x + el.w * s * 0.2, y + el.h * s * 0.5);
                    ctx.lineTo(x + el.w * s * 0.42, y + el.h * s * 0.75);
                    ctx.lineTo(x + el.w * s * 0.8, y + el.h * s * 0.25);
                    ctx.stroke();
                }
            } else if (el.value) {
                const fs = Math.max(el.fontSize * s, 1);
                ctx.font = `${fs}px Helvetica, Arial, sans-serif`;
                ctx.fillStyle = '#111';
                ctx.textBaseline = 'middle';
                ctx.fillText(el.value, x + TEXT_PAD * s, y + (el.h * s) / 2, el.w * s - 2 * TEXT_PAD * s);
            }
        } else {
            ctx.fillStyle = el.fill;
            if (el.type === 'rect') {
                ctx.fillRect(x, y, el.w * s, el.h * s);
                if (el.strokeWidth > 0) {
                    ctx.strokeStyle = el.stroke;
                    ctx.lineWidth = Math.max(el.strokeWidth * s, 0.5);
                    ctx.strokeRect(x, y, el.w * s, el.h * s);
                }
            } else {
                ctx.beginPath();
                ctx.ellipse(x + (el.w * s) / 2, y + (el.h * s) / 2, (el.w * s) / 2, (el.h * s) / 2, 0, 0, Math.PI * 2);
                ctx.fill();
                if (el.strokeWidth > 0) {
                    ctx.strokeStyle = el.stroke;
                    ctx.lineWidth = Math.max(el.strokeWidth * s, 0.5);
                    ctx.stroke();
                }
            }
        }
    }
}

function setActiveThumb(index: number): void {
    const strip = document.getElementById('page-strip');
    if (!strip) return;
    strip.querySelectorAll('.page-thumb').forEach((t, i) => t.classList.toggle('active', i === index));
}

/** Index of the page currently centered-ish in the workspace viewport. */
function currentPageIndex(): number {
    const ws = document.getElementById('workspace');
    if (!ws || state.pages.length === 0) return 0;
    const line = ws.getBoundingClientRect().top + ws.clientHeight * 0.35;
    let idx = 0;
    state.pages.forEach((p, i) => {
        if (p.node.getBoundingClientRect().top <= line) idx = i;
    });
    return idx;
}

function updateActiveThumbFromScroll(): void {
    if (state.pages.length === 0) return;
    setActiveThumb(currentPageIndex());
}

let dragFromIndex: number | null = null;

/* ---- split: multi-select pages in the strip, export just those as a new PDF ----
 *
 * Reuses exportPdf(pages) unchanged, just with a filtered list — the current
 * document is never modified by this.
 */

let selectMode = false;
const selectedPages = new Set<Page>();

/** Called when a document is (re)loaded — old Page objects are about to be discarded. */
function resetPageSelection(): void {
    selectMode = false;
    selectedPages.clear();
    document.getElementById('btn-select-mode')?.classList.remove('active');
    document.getElementById('split-bar')?.classList.add('hidden');
}

function setSelectMode(on: boolean): void {
    selectMode = on;
    if (!on) selectedPages.clear();
    $<HTMLButtonElement>('#btn-select-mode').classList.toggle('active', on);
    $('#split-bar').classList.toggle('hidden', !on);
    updateSplitBar();
    buildPageStrip();
}

function togglePageSelection(page: Page): void {
    if (selectedPages.has(page)) selectedPages.delete(page);
    else selectedPages.add(page);
    updateSplitBar();
    buildPageStrip();
}

function selectAllPages(): void {
    for (const p of state.pages) selectedPages.add(p);
    updateSplitBar();
    buildPageStrip();
}

function clearPageSelection(): void {
    selectedPages.clear();
    updateSplitBar();
    buildPageStrip();
}

function updateSplitBar(): void {
    const n = selectedPages.size;
    const countEl = document.getElementById('split-count');
    if (countEl) countEl.textContent = `${n} selected`;
    const exportBtn = document.getElementById('split-export') as HTMLButtonElement | null;
    if (exportBtn) exportBtn.disabled = n === 0;
}

async function exportSelectedPages(): Promise<void> {
    const pages = state.pages.filter((p) => selectedPages.has(p));
    if (pages.length === 0) return;
    const btn = $<HTMLButtonElement>('#split-export');
    btn.disabled = true;
    const original = btn.textContent;
    btn.textContent = 'Exporting…';
    try {
        const bytes = await exportPdf(pages);
        const blob = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${docNameInput.value.trim() || 'document'}-selection.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
        showToast(`Exported ${pages.length} page${pages.length === 1 ? '' : 's'}`);
    } catch (err) {
        console.error(err);
        showToast('Export failed');
    } finally {
        btn.disabled = selectedPages.size === 0;
        btn.textContent = original;
    }
}

function buildPageStrip(): void {
    const strip = document.getElementById('page-strip');
    if (!strip) return;
    strip.innerHTML = '';
    state.pages.forEach((page, i) => {
        const item = document.createElement('div');
        item.className = 'page-thumb';
        item.classList.toggle('selected', selectedPages.has(page));
        item.draggable = !selectMode && state.pages.length > 1;

        const canvas = document.createElement('canvas');
        item.appendChild(canvas);

        if (selectMode) {
            const check = document.createElement('div');
            check.className = 'page-thumb-check';
            check.innerHTML =
                '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7"/></svg>';
            item.appendChild(check);
        }

        const num = document.createElement('span');
        num.className = 'page-thumb-num';
        num.textContent = String(i + 1);
        item.appendChild(num);

        renderThumb(page, canvas);
        item.addEventListener('click', () => {
            if (selectMode) {
                togglePageSelection(page);
                return;
            }
            page.node.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setActiveThumb(i);
        });

        // Drag-to-reorder (disabled while selecting pages to export). The
        // dragged index lives in a module var rather than dataTransfer since
        // source and target are always this same list.
        item.addEventListener('dragstart', (e) => {
            if (selectMode) return;
            dragFromIndex = i;
            item.classList.add('dragging');
            if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
        });
        item.addEventListener('dragend', () => {
            dragFromIndex = null;
            item.classList.remove('dragging');
            strip.querySelectorAll('.page-thumb').forEach((t) => t.classList.remove('drag-over'));
        });
        item.addEventListener('dragover', (e) => {
            if (dragFromIndex === null) return;
            e.preventDefault();
            if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
            item.classList.add('drag-over');
        });
        item.addEventListener('dragleave', () => item.classList.remove('drag-over'));
        item.addEventListener('drop', (e) => {
            e.preventDefault();
            item.classList.remove('drag-over');
            if (dragFromIndex === null || dragFromIndex === i) return;
            reorderPageWithHistory(dragFromIndex, i);
        });

        strip.appendChild(item);
    });
    updateActiveThumbFromScroll();
}

function refreshThumb(index: number): void {
    const strip = document.getElementById('page-strip');
    const canvas = strip?.children[index]?.querySelector('canvas');
    const page = state.pages[index];
    if (canvas && page) renderThumb(page, canvas as HTMLCanvasElement);
}

let thumbTimer: ReturnType<typeof setTimeout> | undefined;
/** Coalesce rapid edits into a single strip refresh. */
function scheduleStripRefresh(): void {
    clearTimeout(thumbTimer);
    thumbTimer = setTimeout(buildPageStrip, 250);
}

/* ============================================================ search
 *
 * Matches against the same TextEl.text every extracted PDF text run and OCR'd
 * line already lives in — no separate index, just a linear scan on demand.
 */

let searchBar: HTMLDivElement;
let searchInput: HTMLInputElement;
let searchCount: HTMLSpanElement;
let searchMatches: TextEl[] = [];
let searchIndex = -1;

function collectSearchMatches(query: string): TextEl[] {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const matches: TextEl[] = [];
    for (const page of state.pages) {
        for (const el of page.elements) {
            if (el.type === 'text' && el.text.toLowerCase().includes(q)) matches.push(el);
        }
    }
    return matches;
}

function clearSearchHighlights(): void {
    for (const el of searchMatches) el.node.classList.remove('search-match', 'search-match-current');
}

function updateSearchCount(): void {
    searchCount.textContent =
        searchMatches.length === 0 ? (searchInput.value.trim() ? 'No matches' : '') : `${searchIndex + 1} of ${searchMatches.length}`;
}

function focusCurrentMatch(): void {
    for (const el of searchMatches) el.node.classList.remove('search-match-current');
    if (searchIndex < 0 || !searchMatches[searchIndex]) return;
    const el = searchMatches[searchIndex];
    el.node.classList.add('search-match-current');
    el.node.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function runSearch(): void {
    clearSearchHighlights();
    searchMatches = collectSearchMatches(searchInput.value);
    searchIndex = searchMatches.length > 0 ? 0 : -1;
    for (const el of searchMatches) el.node.classList.add('search-match');
    updateSearchCount();
    focusCurrentMatch();
}

function searchNext(): void {
    if (searchMatches.length === 0) return;
    searchIndex = (searchIndex + 1) % searchMatches.length;
    focusCurrentMatch();
    updateSearchCount();
}

function searchPrev(): void {
    if (searchMatches.length === 0) return;
    searchIndex = (searchIndex - 1 + searchMatches.length) % searchMatches.length;
    focusCurrentMatch();
    updateSearchCount();
}

function openSearch(): void {
    searchBar.classList.remove('hidden');
    searchInput.focus();
    searchInput.select();
    if (searchInput.value) runSearch();
}

function closeSearch(): void {
    searchBar.classList.add('hidden');
    clearSearchHighlights();
    searchMatches = [];
    searchIndex = -1;
}

/** Called when a document is (re)loaded — old TextEl nodes are about to be discarded. */
function resetSearch(): void {
    searchMatches = [];
    searchIndex = -1;
    searchBar?.classList.add('hidden');
    if (searchInput) searchInput.value = '';
    if (searchCount) searchCount.textContent = '';
}

/* ============================================================ zoom */

let zoomLabel: HTMLSpanElement;

function setZoom(z: number): void {
    state.zoom = Math.min(Math.max(z, 0.4), 2.5);
    zoomLabel.textContent = `${Math.round(state.zoom * 100)}%`;
    for (const page of state.pages) {
        page.node.style.width = `${page.width * state.zoom}px`;
        page.node.style.height = `${page.height * state.zoom}px`;
        for (const el of page.elements) syncElNode(el);
    }
}

/* ============================================================ pages */

function newPage(width: number, height: number, srcIndex: number | null): Page {
    return {
        width,
        height,
        srcIndex,
        elements: [],
        erasures: [],
        node: document.createElement('div'),
        bgCanvas: document.createElement('canvas'),
        elLayer: document.createElement('div'),
    };
}

async function addBlankPage(): Promise<void> {
    const [w, h] =
        state.pages.length > 0
            ? [state.pages[state.pages.length - 1].width, state.pages[state.pages.length - 1].height]
            : state.defaultPageSize;
    const page = newPage(w, h, null);
    state.pages.push(page);
    rebuildPages();
    await renderPageBackground(page);
    pushCommand({ kind: 'page-add', page, index: state.pages.indexOf(page) });
    page.node.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * Appends every page of another PDF file to the end of the current document.
 * Loaded via a temporary pdf.js document that's discarded once extraction is
 * done — inserted pages are never tracked as part of state.pdfjsDoc, so they
 * always export via their own raster background (see Page.rasterBg).
 */
async function insertPdfPages(bytes: Uint8Array): Promise<void> {
    showToast('Inserting PDF…');
    let doc: pdfjsLib.PDFDocumentProxy;
    try {
        doc = await pdfjsLib.getDocument({ data: bytes.slice() }).promise;
    } catch (err) {
        console.error('could not open PDF to merge', err);
        showToast('Could not open that PDF');
        return;
    }

    const inserted: Page[] = [];
    for (let i = 0; i < doc.numPages; i++) {
        const pdfPage = await doc.getPage(i + 1);
        const vp = pdfPage.getViewport({ scale: 1 });
        const page = newPage(vp.width, vp.height, null);
        page.rasterBg = true;
        state.pages.push(page);
        inserted.push(page);
    }
    rebuildPages();
    buildPageStrip(); // create the (blank) thumbnails up front, same as opening a PDF

    for (let i = 0; i < inserted.length; i++) {
        const pdfPage = await doc.getPage(i + 1);
        await renderMergedPageBackground(inserted[i], pdfPage);
        refreshThumb(state.pages.indexOf(inserted[i]));
    }
    for (const page of inserted) {
        pushCommand({ kind: 'page-add', page, index: state.pages.indexOf(page) });
    }

    inserted[0]?.node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    showToast(`Inserted ${inserted.length} page${inserted.length === 1 ? '' : 's'}`);
}

function deletePage(page: Page): void {
    if (state.pages.length <= 1) return;
    if (page.elements.length > 0 && !confirm('Delete this page and everything on it?')) return;
    const index = state.pages.indexOf(page);
    removePage(page);
    pushCommand({ kind: 'page-delete', page, index });
}

/* ============================================================ document lifecycle */

async function startNewDocument(size: readonly number[]): Promise<void> {
    state.pages = [];
    state.originalPdfBytes = null;
    state.pdfjsDoc = null;
    state.docId = null;
    state.shareToken = null;
    state.sharePermission = null;
    state.defaultPageSize = size;
    select(null);
    resetHistory();
    resetSearch();
    resetPageSelection();
    docNameInput.value = 'Untitled document';
    const page = newPage(size[0], size[1], null);
    state.pages.push(page);
    rebuildPages();
    await renderPageBackground(page);
    fitZoom();
    buildPageStrip();
    applyPermissionUI();
}

async function openPdfBytes(
    bytes: Uint8Array,
    name: string,
    docId: number | null,
    shareCtx?: { token: string | null; permission: 'view' | 'edit' | null },
): Promise<void> {
    showToast('Opening PDF…');
    const doc = await pdfjsLib.getDocument({ data: bytes.slice() }).promise;
    state.originalPdfBytes = bytes;
    state.pdfjsDoc = doc;
    state.docId = docId;
    // Omitted (as from restoreVersion, an in-place refresh) preserves whatever
    // share context is already active rather than clearing it.
    if (shareCtx) {
        state.shareToken = shareCtx.token;
        state.sharePermission = shareCtx.permission;
    }
    state.pages = [];
    select(null);
    resetHistory();
    resetSearch();
    resetPageSelection();
    docNameInput.value = name.replace(/\.pdf$/i, '');

    for (let i = 0; i < doc.numPages; i++) {
        const pdfPage = await doc.getPage(i + 1);
        const vp = pdfPage.getViewport({ scale: 1 });
        state.pages.push(newPage(vp.width, vp.height, i));
    }
    rebuildPages();
    fitZoom();
    buildPageStrip(); // create the (blank) thumbnails up front
    for (let i = 0; i < state.pages.length; i++) {
        await renderPageBackground(state.pages[i]);
        refreshThumb(i); // fill in each thumbnail as its render/extraction completes
    }
    try {
        await detectFormFields(bytes, state.pages);
    } catch (err) {
        console.error('form field detection failed', err);
    }
    buildPageStrip(); // refresh thumbnails once more in case any fields were detected
    showToast('PDF converted — click any text or image to edit it');
    setHint('Every text run and image is now an editable element: click to select and move, double-click text to edit inline, Delete to remove.');
    setTimeout(() => { if (!state.pendingTool) setHint(null); }, 12000);
    applyPermissionUI();
}

/** Disables saving for a document opened with view-only share access. */
function applyPermissionUI(): void {
    const viewOnly = state.sharePermission === 'view';
    const saveBtn = document.getElementById('btn-save') as HTMLButtonElement | null;
    if (saveBtn) saveBtn.disabled = viewOnly;
    docNameInput.disabled = viewOnly;
    if (viewOnly) setSaveStatus('View only — changes here won\'t be saved');
}

function fitZoom(): void {
    const maxPageW = Math.max(...state.pages.map((p) => p.width));
    const available = $('#workspace').clientWidth - 100;
    setZoom(Math.min(available / maxPageW, 1.5));
}

/**
 * Uploads the current export to the server (creating the document on first
 * save, updating it thereafter). Shared by the explicit Save button and
 * autosave — the server snapshots the outgoing file as a version on every
 * update (see DocumentController::update()), so both paths feed version
 * history automatically.
 */
async function persistDocument(): Promise<DocumentMeta> {
    if (state.sharePermission === 'view') throw new Error('This document was shared as view-only');
    const bytes = await exportPdf();
    const form = new FormData();
    form.append('name', docNameInput.value.trim() || 'Untitled document');
    form.append('user_id', String(getCurrentUserId() ?? ''));
    if (state.shareToken) form.append('token', state.shareToken);
    form.append('pdf', new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' }), 'document.pdf');
    const url = state.docId ? `/api/documents/${state.docId}` : '/api/documents';
    const res = await fetch(url, { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf, Accept: 'application/json' }, body: form });
    if (!res.ok) throw new Error(`save failed: ${res.status}`);
    const doc = (await res.json()) as DocumentMeta;
    state.docId = doc.id;
    lastSavedHistoryIndex = historyIndex;
    return doc;
}

async function saveToServer(): Promise<void> {
    stopEditing();
    const btn = $<HTMLButtonElement>('#btn-save');
    btn.disabled = true;
    btn.textContent = 'Saving…';
    try {
        await persistDocument();
        showToast('Saved ✓');
        setSaveStatus('Saved just now');
    } catch (err) {
        console.error(err);
        showToast('Save failed');
    } finally {
        btn.disabled = false;
        btn.textContent = 'Save';
    }
}

/* ---- autosave ----
 *
 * Only runs once a document has been saved at least once (has a docId) — a
 * brand-new document waits for an explicit first Save so the user picks a
 * name and confirms it's worth persisting. From then on, any committed edit
 * (re)starts a debounce timer; if nothing else changes for AUTOSAVE_DEBOUNCE_MS,
 * it silently persists.
 */

const AUTOSAVE_DEBOUNCE_MS = 15000;
let lastSavedHistoryIndex = 0;
let autosaveTimer: ReturnType<typeof setTimeout> | undefined;

function scheduleAutosave(): void {
    if (state.docId === null) return;
    clearTimeout(autosaveTimer);
    autosaveTimer = setTimeout(() => void autosaveTick(), AUTOSAVE_DEBOUNCE_MS);
}

async function autosaveTick(): Promise<void> {
    if (state.docId === null || historyIndex === lastSavedHistoryIndex || state.editing) return;
    if (state.sharePermission === 'view') return;
    try {
        await persistDocument();
        setSaveStatus('Autosaved just now');
    } catch (err) {
        console.error('autosave failed', err);
        setSaveStatus('Autosave failed — click Save');
    }
}

function setSaveStatus(text: string): void {
    const el = document.getElementById('save-status');
    if (el) el.textContent = text;
}

/* ---- version history ---- */

interface DocumentVersionMeta {
    id: number;
    size: number;
    created_at: string;
}

function docApiUrl(path: string): string {
    const userId = getCurrentUserId();
    let url = `/api/documents/${state.docId}${path}?user_id=${encodeURIComponent(String(userId ?? ''))}`;
    if (state.shareToken) url += `&token=${encodeURIComponent(state.shareToken)}`;
    return url;
}

function formatVersionTime(iso: string): string {
    const d = new Date(iso);
    const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return `${diffH} hr ago`;
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

async function openHistoryModal(): Promise<void> {
    const modal = $<HTMLDivElement>('#history-modal');
    const host = $<HTMLDivElement>('#version-list');
    if (state.docId === null) {
        showToast('Save the document first to see its version history');
        return;
    }
    modal.classList.remove('hidden');
    host.innerHTML = '<div class="text-secondary text-center py-3 small">Loading…</div>';

    try {
        const res = await fetch(docApiUrl('/versions'), { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(String(res.status));
        const versions = (await res.json()) as DocumentVersionMeta[];
        if (versions.length === 0) {
            host.innerHTML = '<div class="text-secondary text-center py-3 small">No earlier versions yet — versions appear after the first edit following a save.</div>';
            return;
        }
        host.innerHTML = '';
        for (const v of versions) {
            const row = document.createElement('div');
            row.className = 'list-group-item d-flex align-items-center gap-2 px-2';
            const kb = Math.max(1, Math.round(v.size / 1024));
            row.innerHTML =
                `<span class="fs-4">🕓</span>` +
                `<div class="flex-fill"><div>${formatVersionTime(v.created_at)}</div><div class="text-secondary small">${kb} KB</div></div>` +
                `<button class="btn btn-sm btn-outline-secondary version-download">Download</button>` +
                `<button class="btn btn-sm btn-primary version-restore">Restore</button>`;
            row.querySelector<HTMLButtonElement>('.version-download')!.addEventListener('click', () => {
                window.open(docApiUrl(`/versions/${v.id}/file`), '_blank');
            });
            row.querySelector<HTMLButtonElement>('.version-restore')!.addEventListener('click', () => void restoreVersion(v.id));
            host.appendChild(row);
        }
    } catch (err) {
        console.error(err);
        host.innerHTML = '<div class="text-danger text-center py-3 small">Could not load version history.</div>';
    }
}

async function restoreVersion(versionId: number): Promise<void> {
    if (!confirm('Restore this version? The current content will be kept as a version too, so nothing is lost.')) return;
    try {
        const res = await fetch(docApiUrl(`/versions/${versionId}/restore`), {
            method: 'POST',
            headers: { 'X-CSRF-TOKEN': csrf, Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(String(res.status));

        const fileRes = await fetch(docApiUrl('/file'));
        if (!fileRes.ok) throw new Error(String(fileRes.status));
        const bytes = new Uint8Array(await fileRes.arrayBuffer());
        const name = docNameInput.value.trim() || 'document';
        const docId = state.docId;
        $<HTMLDivElement>('#history-modal').classList.add('hidden');
        await openPdfBytes(bytes, name, docId);
        showToast('Version restored');
    } catch (err) {
        console.error(err);
        showToast('Restore failed');
    }
}

/* ---- sharing ---- */

interface ShareMeta {
    id: number;
    shared_with_email: string | null;
    share_token: string | null;
    permission: 'view' | 'edit';
    created_at: string;
}

async function openShareModal(): Promise<void> {
    if (state.docId === null) {
        showToast('Save the document first to share it');
        return;
    }
    $<HTMLDivElement>('#share-modal').classList.remove('hidden');
    await loadShares();
}

async function loadShares(): Promise<void> {
    const host = $<HTMLDivElement>('#share-list');
    host.innerHTML = '<div class="text-secondary text-center py-3 small">Loading…</div>';
    try {
        const res = await fetch(docApiUrl('/shares'), { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(String(res.status));
        const shares = (await res.json()) as ShareMeta[];
        if (shares.length === 0) {
            host.innerHTML = '<div class="text-secondary text-center py-3 small">Not shared with anyone yet.</div>';
            return;
        }
        host.innerHTML = '';
        for (const share of shares) {
            const row = document.createElement('div');
            row.className = 'list-group-item d-flex align-items-center gap-2 px-2';
            const label = share.share_token
                ? `Anyone with the link — ${location.origin}/#/shared?token=${share.share_token}`
                : (share.shared_with_email ?? 'Unknown');
            row.innerHTML =
                `<span class="fs-4">${share.share_token ? '🔗' : '👤'}</span>` +
                `<div class="flex-fill text-truncate small">${label}</div>` +
                `<span class="badge bg-${share.permission === 'edit' ? 'primary' : 'secondary'}-lt flex-none">${share.permission}</span>` +
                (share.share_token ? `<button class="btn btn-sm btn-outline-secondary share-copy flex-none">Copy</button>` : '') +
                `<button class="btn btn-sm btn-outline-danger share-revoke flex-none">Revoke</button>`;
            row.querySelector<HTMLButtonElement>('.share-copy')?.addEventListener('click', () => {
                void navigator.clipboard.writeText(`${location.origin}/#/shared?token=${share.share_token}`);
                showToast('Link copied');
            });
            row.querySelector<HTMLButtonElement>('.share-revoke')!.addEventListener('click', () => void revokeShare(share.id));
            host.appendChild(row);
        }
    } catch (err) {
        console.error(err);
        host.innerHTML = '<div class="text-danger text-center py-3 small">Could not load shares.</div>';
    }
}

async function createMemberShare(): Promise<void> {
    const email = $<HTMLInputElement>('#share-email').value.trim();
    const permission = $<HTMLSelectElement>('#share-permission').value;
    if (!email) {
        showToast('Enter an email to share with');
        return;
    }
    const form = new FormData();
    form.append('mode', 'member');
    form.append('email', email);
    form.append('permission', permission);
    form.append('user_id', String(getCurrentUserId() ?? ''));
    try {
        const res = await fetch(docApiUrl('/shares'), { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf, Accept: 'application/json' }, body: form });
        if (!res.ok) throw new Error(String(res.status));
        $<HTMLInputElement>('#share-email').value = '';
        showToast('Shared ✓');
        await loadShares();
    } catch (err) {
        console.error(err);
        showToast('Could not share — check the email is a registered member');
    }
}

async function createLinkShare(): Promise<void> {
    const permission = $<HTMLSelectElement>('#share-link-permission').value;
    const form = new FormData();
    form.append('mode', 'link');
    form.append('permission', permission);
    form.append('user_id', String(getCurrentUserId() ?? ''));
    try {
        const res = await fetch(docApiUrl('/shares'), { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf, Accept: 'application/json' }, body: form });
        if (!res.ok) throw new Error(String(res.status));
        await loadShares();
    } catch (err) {
        console.error(err);
        showToast('Could not create link');
    }
}

async function revokeShare(shareId: number): Promise<void> {
    try {
        const res = await fetch(docApiUrl(`/shares/${shareId}`), { method: 'DELETE', headers: { 'X-CSRF-TOKEN': csrf } });
        if (!res.ok) throw new Error(String(res.status));
        await loadShares();
    } catch (err) {
        console.error(err);
        showToast('Could not revoke share');
    }
}

/* ============================================================ export via pdf-lib
 *
 * Pages copied from the original PDF keep their original (vector) content.
 * Extracted elements that were never touched are skipped — the original
 * rendering stays. Anything modified or deleted gets its original region
 * whited out and (if modified) redrawn at its current position/style.
 */

const FONT_VARIANTS: Record<FontFamily, [StandardFonts, StandardFonts, StandardFonts, StandardFonts]> = {
    Helvetica: [StandardFonts.Helvetica, StandardFonts.HelveticaBold, StandardFonts.HelveticaOblique, StandardFonts.HelveticaBoldOblique],
    Times: [StandardFonts.TimesRoman, StandardFonts.TimesRomanBold, StandardFonts.TimesRomanItalic, StandardFonts.TimesRomanBoldItalic],
    Courier: [StandardFonts.Courier, StandardFonts.CourierBold, StandardFonts.CourierOblique, StandardFonts.CourierBoldOblique],
};

/**
 * True when a page has edited or deleted *original* content. Such a page can't
 * be exported by copying the source page and whiting-out the changed regions:
 * a white rectangle hides text visually but leaves it in the content stream, so
 * reopening the saved PDF would re-extract the hidden original alongside the
 * replacement (the two "merge"). These pages are instead flattened to their
 * erased raster background — which has no text layer — and every element is
 * redrawn on top, so each piece of content exists exactly once.
 */
function pageNeedsFlatten(page: Page): boolean {
    return (
        page.erasures.length > 0 ||
        page.elements.some((el) => el.src != null && el.dirty) ||
        // A redaction box only truly removes what's beneath it if the page has no
        // text/vector layer left to re-extract from — force the same rasterize path
        // used for edited/deleted original content.
        page.elements.some((el) => el.type === 'redact') ||
        // A merged-in page has no source doc to copy verbatim from — it always
        // exports as its own raster background with every element redrawn on top.
        page.rasterBg === true
    );
}

/**
 * The unrotated text anchor (baseline start) pdf-lib's `rotate` option pivots
 * around — but the editor rotates the whole box around its *center* (default
 * CSS transform-origin). This re-derives the anchor point so pdf-lib's
 * around-the-anchor rotation lands in the same place as the editor's
 * around-the-center rotation: rotate the original anchor around the box
 * center by the same angle, and use that as the new anchor.
 */
function rotatedTextAnchor(el: TextEl, pageH: number): { x: number; y: number } {
    const ox = el.x + TEXT_PAD;
    const oy = pageH - (el.y + TEXT_PAD) - el.fontSize * BASELINE_RATIO;
    if (!el.rotation) return { x: ox, y: oy };
    const cx = el.x + el.w / 2;
    const cy = pageH - (el.y + el.h / 2);
    const theta = (el.rotation * Math.PI) / 180;
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);
    const dx = ox - cx;
    const dy = oy - cy;
    return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
}

/** Exports the given pages (defaulting to the whole document) as a new PDF's bytes. */
async function exportPdf(pages: Page[] = state.pages): Promise<Uint8Array> {
    const out = await PDFDocument.create();
    const original = state.originalPdfBytes ? await PDFDocument.load(state.originalPdfBytes) : null;

    // Assemble pages in editor (or given subset) order. Untouched source pages
    // are copied verbatim (crisp vectors, original fonts); edited/new pages
    // start blank.
    for (const page of pages) {
        if (page.srcIndex !== null && original && !pageNeedsFlatten(page)) {
            const [copied] = await out.copyPages(original, [page.srcIndex]);
            out.addPage(copied);
        } else {
            out.addPage([page.width, page.height]);
        }
    }

    const fontCache = new Map<StandardFonts, PDFFont>();
    const embedFont = async (name: StandardFonts): Promise<PDFFont> => {
        let f = fontCache.get(name);
        if (!f) {
            f = await out.embedFont(name);
            fontCache.set(name, f);
        }
        return f;
    };

    for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        const outPage = out.getPage(i);
        const pageH = page.height;
        const flatten = pageNeedsFlatten(page);

        const whiteout = (box: Box): void => {
            outPage.drawRectangle({
                x: box.x,
                y: pageH - box.y - box.h,
                width: box.w,
                height: box.h,
                color: rgb(1, 1, 1),
            });
        };

        // Flattened source page: lay down the erased render (non-extracted
        // vector art / undecodable images, with extracted text & images already
        // whited out of the pixels) as a text-free raster background. Every
        // element — even untouched ones — is then redrawn on top below. Merged
        // pages (rasterBg) have no source doc to fall back on, so they always
        // take this path too, even though their srcIndex is null.
        if ((flatten && page.srcIndex !== null) || page.rasterBg) {
            const bg = await out.embedPng(dataUrlToBytes(page.bgCanvas.toDataURL('image/png')));
            outPage.drawImage(bg, { x: 0, y: 0, width: page.width, height: page.height });
        }

        // On a flattened page the raster already reflects deletions, so the
        // whiteouts are only needed when copying the vector source page.
        if (!flatten) {
            for (const box of page.erasures) whiteout(box);
        }

        for (const el of page.elements) {
            // On a copied (non-flattened) page, untouched extracted content is
            // already shown perfectly by the source page — leave it be. When
            // flattening, the source text layer is gone, so redraw everything.
            if (el.src && !el.dirty && !flatten) continue;
            // Copied page: hide the original text before drawing the replacement.
            // (Flattened pages have no text layer to hide.)
            if (el.src && !flatten) whiteout(el.src);

            if (el.type === 'text') {
                const variant = FONT_VARIANTS[el.fontFamily][(el.bold ? 1 : 0) + (el.italic ? 2 : 0)];
                const font = await embedFont(variant);
                if (el.whiteBg) whiteout({ x: el.x, y: el.y, w: el.w, h: el.h });
                const { r, g, b } = hexToRgb(el.color);
                let text = el.text;
                const anchor = rotatedTextAnchor(el, pageH);
                const opts = {
                    x: anchor.x,
                    y: anchor.y,
                    size: el.fontSize,
                    font,
                    color: rgb(r, g, b),
                    lineHeight: el.fontSize * 1.2,
                    rotate: degrees(el.rotation ?? 0),
                    // OCR text layer: present (searchable/selectable) but invisible —
                    // the scanned image underneath is the only thing actually seen.
                    // Otherwise honor an explicit opacity (used by watermarks).
                    opacity: el.ocr ? 0 : (el.opacity ?? 1),
                };
                try {
                    outPage.drawText(text, opts);
                } catch {
                    // Standard fonts only support WinAnsi — replace anything they can't encode.
                    text = text.replace(/[^\x00-\xFF]/g, '?');
                    outPage.drawText(text, opts);
                }
            } else if (el.type === 'image') {
                const bytes = dataUrlToBytes(el.dataUrl);
                const img = el.isJpeg ? await out.embedJpg(bytes) : await out.embedPng(bytes);
                outPage.drawImage(img, { x: el.x, y: pageH - el.y - el.h, width: el.w, height: el.h });
            } else if (el.type === 'redact') {
                // Solid and opaque — the page was already forced to flatten (see
                // pageNeedsFlatten), so there is no text/vector layer left underneath
                // to leak; this box just guarantees full pixel coverage on top of it.
                outPage.drawRectangle({ x: el.x, y: pageH - el.y - el.h, width: el.w, height: el.h, color: rgb(0, 0, 0) });
            } else if (el.type === 'path') {
                // pdf-lib flips the Y axis internally for SVG paths, so (x, y) here is
                // the anchor the path's own (0,0) — i.e. el's top-left corner — maps to,
                // in normal bottom-up PDF space: pageH - el.y (not - el.h; el.y is the top).
                const { r, g, b } = hexToRgb(el.color);
                outPage.drawSvgPath(buildSmoothPath(el.points), {
                    x: el.x,
                    y: pageH - el.y,
                    borderColor: rgb(r, g, b),
                    borderWidth: el.strokeWidth,
                    borderOpacity: el.mode === 'highlighter' ? HIGHLIGHTER_DEFAULTS.opacity : 1,
                    // True multiply blend for the highlighter — lets underlying text
                    // show through like a real marker, not just a faded box on top.
                    blendMode: el.mode === 'highlighter' ? BlendMode.Multiply : undefined,
                });
            } else if (el.type === 'field') {
                // Flattened, static content — see FieldEl and
                // apps/www/_docs/pdf_editor_form_filling_todo.md for the interactive-fields tradeoff.
                const helv = await embedFont(StandardFonts.Helvetica);
                if (el.fieldType === 'checkbox') {
                    outPage.drawRectangle({
                        x: el.x,
                        y: pageH - el.y - el.h,
                        width: el.w,
                        height: el.h,
                        borderColor: rgb(0.25, 0.25, 0.25),
                        borderWidth: 1,
                    });
                    if (el.checked) {
                        const size = Math.min(el.w, el.h) * 0.75;
                        outPage.drawText('X', {
                            x: el.x + (el.w - size * 0.55) / 2,
                            y: pageH - el.y - el.h / 2 - size * 0.32,
                            size,
                            font: helv,
                            color: rgb(0.1, 0.1, 0.1),
                        });
                    }
                } else if (el.value) {
                    // Center the baseline vertically within the widget's rect (which is
                    // usually taller than one line of text), unlike TextEl's top-anchored math.
                    const vPad = Math.max((el.h - el.fontSize) / 2, 0);
                    let text = el.value;
                    const opts = {
                        x: el.x + TEXT_PAD,
                        y: pageH - (el.y + vPad) - el.fontSize * BASELINE_RATIO,
                        size: el.fontSize,
                        font: helv,
                        color: rgb(0.05, 0.05, 0.05),
                    };
                    try {
                        outPage.drawText(text, opts);
                    } catch {
                        text = text.replace(/[^\x00-\xFF]/g, '?');
                        outPage.drawText(text, opts);
                    }
                }
            } else {
                const fill = hexToRgb(el.fill);
                const stroke = hexToRgb(el.stroke);
                const common = {
                    color: rgb(fill.r, fill.g, fill.b),
                    borderColor: el.strokeWidth > 0 ? rgb(stroke.r, stroke.g, stroke.b) : undefined,
                    borderWidth: el.strokeWidth > 0 ? el.strokeWidth : undefined,
                };
                if (el.type === 'rect') {
                    outPage.drawRectangle({ x: el.x, y: pageH - el.y - el.h, width: el.w, height: el.h, ...common });
                } else {
                    outPage.drawEllipse({
                        x: el.x + el.w / 2,
                        y: pageH - el.y - el.h / 2,
                        xScale: el.w / 2,
                        yScale: el.h / 2,
                        ...common,
                    });
                }
            }
        }
    }

    return out.save();
}

function dataUrlToBytes(dataUrl: string): Uint8Array {
    const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
    const bin = atob(base64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
}

async function downloadPdf(): Promise<void> {
    stopEditing();
    const btn = $<HTMLButtonElement>('#btn-download');
    btn.disabled = true;
    btn.textContent = 'Exporting…';
    try {
        const bytes = await exportPdf();
        const blob = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${docNameInput.value.trim() || 'document'}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
    } catch (err) {
        console.error(err);
        showToast('Export failed');
    } finally {
        btn.disabled = false;
        btn.textContent = 'Download PDF';
    }
}

/* ============================================================ keyboard */

document.addEventListener('keydown', (e) => {
    if (state.editing || document.activeElement instanceof HTMLInputElement || document.activeElement instanceof HTMLSelectElement) {
        if (e.key === 'Escape' && state.editing) stopEditing();
        return;
    }
    if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
    }
    if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redo();
        return;
    }
    if ((e.metaKey || e.ctrlKey) && !e.altKey) {
        const k = e.key.toLowerCase();
        if (k === 'c' && state.selected) { e.preventDefault(); copyEl(state.selected); return; }
        if (k === 'v' && clipboard) { e.preventDefault(); pasteCascade(); return; }
        if (k === 'd' && state.selected) { e.preventDefault(); duplicateEl(state.selected); return; }
        if (k === 'f') { e.preventDefault(); openSearch(); return; }
    }
    if (e.key === 'Escape') {
        hideContextMenu();
        if (!searchBar.classList.contains('hidden')) closeSearch();
        if (state.pendingTool) setPendingTool(null);
        else select(null);
    } else if ((e.key === 'Delete' || e.key === 'Backspace') && state.selected) {
        e.preventDefault();
        deleteElWithHistory(state.selected);
    } else if (e.key.startsWith('Arrow') && state.selected) {
        e.preventDefault();
        const before = snapshotEl(state.selected);
        const step = e.shiftKey ? 10 : 1;
        if (e.key === 'ArrowLeft') state.selected.x -= step;
        if (e.key === 'ArrowRight') state.selected.x += step;
        if (e.key === 'ArrowUp') state.selected.y -= step;
        if (e.key === 'ArrowDown') state.selected.y += step;
        markDirty(state.selected);
        syncElNode(state.selected);
        pushElChange(state.selected, before);
    } else if (e.key === 'Enter' && state.selected?.type === 'text') {
        e.preventDefault();
        startEditing(state.selected);
    }
});

/* ============================================================ mount
 *
 * Called by the "editor" view's listener (see views.ts) each time that view
 * is rendered into the shell's content container. The container's markup is
 * rebuilt fresh on every navigation, so all DOM refs are (re)acquired here
 * and every listener below is (re)attached to the fresh nodes — nothing here
 * changes how the PDF itself is extracted, edited, or exported.
 */

export function mountEditor(params: EditorMountParams = {}): void {
    pagesHost = $<HTMLDivElement>('#pages');
    docNameInput = $<HTMLInputElement>('#doc-name');
    hintBar = $<HTMLDivElement>('#hint-bar');

    searchBar = $<HTMLDivElement>('#search-bar');
    searchInput = $<HTMLInputElement>('#search-input');
    searchCount = $<HTMLSpanElement>('#search-count');
    $('#btn-search').addEventListener('click', () => {
        if (searchBar.classList.contains('hidden')) openSearch();
        else closeSearch();
    });
    $('#search-close').addEventListener('click', closeSearch);
    $('#search-next').addEventListener('click', searchNext);
    $('#search-prev').addEventListener('click', searchPrev);
    searchInput.addEventListener('input', runSearch);
    searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (e.shiftKey) searchPrev();
            else searchNext();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            closeSearch();
        }
    });

    // Keep the page-strip's active highlight in sync with the scroll position.
    let thumbScrollQueued = false;
    $('#workspace').addEventListener('scroll', () => {
        if (thumbScrollQueued) return;
        thumbScrollQueued = true;
        requestAnimationFrame(() => {
            thumbScrollQueued = false;
            updateActiveThumbFromScroll();
        });
    });

    undoBtn = $<HTMLButtonElement>('#btn-undo');
    redoBtn = $<HTMLButtonElement>('#btn-redo');
    undoBtn.addEventListener('click', undo);
    redoBtn.addEventListener('click', redo);

    fontFamilySel = $<HTMLSelectElement>('#font-family');
    fontSizeInput = $<HTMLInputElement>('#font-size');
    boldBtn = $<HTMLButtonElement>('#btn-bold');
    italicBtn = $<HTMLButtonElement>('#btn-italic');
    textColorInput = $<HTMLInputElement>('#text-color');
    textBgBtn = $<HTMLButtonElement>('#btn-textbg');
    fillColorInput = $<HTMLInputElement>('#fill-color');
    strokeColorInput = $<HTMLInputElement>('#stroke-color');
    strokeWidthInput = $<HTMLInputElement>('#stroke-width');

    fontFamilySel.addEventListener('change', () => applyTextProp('fontFamily', fontFamilySel.value as FontFamily));
    fontSizeInput.addEventListener('change', () => applyTextProp('fontSize', Math.min(Math.max(Number(fontSizeInput.value) || 16, 6), 144)));
    boldBtn.addEventListener('click', () => {
        const v = state.selected?.type === 'text' ? !state.selected.bold : !defaults.bold;
        boldBtn.classList.toggle('active', v);
        applyTextProp('bold', v);
    });
    italicBtn.addEventListener('click', () => {
        const v = state.selected?.type === 'text' ? !state.selected.italic : !defaults.italic;
        italicBtn.classList.toggle('active', v);
        applyTextProp('italic', v);
    });
    textColorInput.addEventListener('input', () => applyTextProp('color', textColorInput.value));
    textBgBtn.addEventListener('click', () => {
        const v = state.selected?.type === 'text' ? !state.selected.whiteBg : !defaults.whiteBg;
        textBgBtn.classList.toggle('active', v);
        applyTextProp('whiteBg', v);
    });
    fillColorInput.addEventListener('input', () => applyShapeProp('fill', fillColorInput.value));
    strokeColorInput.addEventListener('input', () => applyShapeProp('stroke', strokeColorInput.value));
    strokeWidthInput.addEventListener('change', () => applyShapeProp('strokeWidth', Math.min(Math.max(Number(strokeWidthInput.value) || 0, 0), 20)));

    toolButtons = document.querySelectorAll<HTMLButtonElement>('[data-tool]');
    toolButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
            const tool = btn.dataset.tool as Tool;
            if (state.pendingTool === tool) {
                setPendingTool(null);
                return;
            }
            if (tool === 'image') {
                imgFileInput.value = '';
                imgFileInput.click();
            } else {
                setPendingTool(tool);
            }
        });
    });

    imgFileInput = $<HTMLInputElement>('#img-file-input');
    imgFileInput.addEventListener('change', () => void handleImageFileChange());

    // ---- stamp modal (watermark / page numbers / header-footer) ----
    $('#btn-stamp').addEventListener('click', openStampModal);
    $('#stamp-close').addEventListener('click', closeStampModal);
    $('#stamp-tab-watermark').addEventListener('click', () => setStampTab('watermark'));
    $('#stamp-tab-pagenum').addEventListener('click', () => setStampTab('pagenum'));
    $('#stamp-tab-headerfooter').addEventListener('click', () => setStampTab('headerfooter'));
    $('#wm-apply').addEventListener('click', applyWatermark);
    $('#pn-apply').addEventListener('click', applyPageNumbers);
    $('#hf-apply').addEventListener('click', applyHeaderFooter);

    // ---- signature modal ----
    $('#btn-signature').addEventListener('click', openSignatureModal);
    $('#sig-close').addEventListener('click', closeSignatureModal);
    $('#sig-cancel').addEventListener('click', closeSignatureModal);
    $('#sig-tab-draw').addEventListener('click', () => setSigMode('draw'));
    $('#sig-tab-type').addEventListener('click', () => { setSigMode('type'); buildSigFontOptions(); });
    $('#sig-clear').addEventListener('click', clearSigCanvas);
    $('#sig-insert').addEventListener('click', confirmSignature);
    $('#sig-use-last').addEventListener('click', useLastSignature);
    $<HTMLInputElement>('#sig-text').addEventListener('input', renderTypedSigPreview);

    const sigCanvas = $<HTMLCanvasElement>('#sig-canvas');
    sigCanvas.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        sigDrawing = true;
        sigCanvas.setPointerCapture(e.pointerId);
        sigStrokes.push([sigPointerPos(sigCanvas, e)]);
    });
    sigCanvas.addEventListener('pointermove', (e) => {
        if (!sigDrawing) return;
        sigStrokes[sigStrokes.length - 1].push(sigPointerPos(sigCanvas, e));
        redrawSigCanvas();
    });
    const stopSigDrawing = (): void => { sigDrawing = false; };
    sigCanvas.addEventListener('pointerup', stopSigDrawing);
    sigCanvas.addEventListener('pointerleave', stopSigDrawing);

    zoomLabel = $<HTMLSpanElement>('#zoom-label');
    $('#zoom-in').addEventListener('click', () => setZoom(state.zoom + 0.15));
    $('#zoom-out').addEventListener('click', () => setZoom(state.zoom - 0.15));

    $('#btn-add-page').addEventListener('click', () => void addBlankPage());

    $('#btn-select-mode').addEventListener('click', () => setSelectMode(!selectMode));
    $('#split-all').addEventListener('click', selectAllPages);
    $('#split-none').addEventListener('click', clearPageSelection);
    $('#split-export').addEventListener('click', () => void exportSelectedPages());
    $('#split-cancel').addEventListener('click', () => setSelectMode(false));

    const pdfInsertInput = $<HTMLInputElement>('#pdf-insert-file-input');
    $('#btn-insert-pdf').addEventListener('click', () => {
        pdfInsertInput.value = '';
        pdfInsertInput.click();
    });
    pdfInsertInput.addEventListener('change', () => {
        const file = pdfInsertInput.files?.[0];
        if (!file) return;
        void file.arrayBuffer().then((buf) => insertPdfPages(new Uint8Array(buf)));
    });

    $('#btn-save').addEventListener('click', () => void saveToServer());
    $('#btn-download').addEventListener('click', () => void downloadPdf());
    $('#btn-history').addEventListener('click', () => void openHistoryModal());
    $('#history-close').addEventListener('click', () => $<HTMLDivElement>('#history-modal').classList.add('hidden'));

    $('#btn-share').addEventListener('click', () => void openShareModal());
    $('#share-close').addEventListener('click', () => $<HTMLDivElement>('#share-modal').classList.add('hidden'));
    $('#share-member-btn').addEventListener('click', () => void createMemberShare());
    $('#share-link-btn').addEventListener('click', () => void createLinkShare());
    setSaveStatus('');

    if (params.action === 'open-bytes' && params.bytes) {
        void openPdfBytes(params.bytes, params.name ?? 'document', params.docId ?? null, {
            token: params.shareToken ?? null,
            permission: params.sharePermission ?? null,
        });
    } else {
        void startNewDocument(params.size ?? PAGE_SIZES.letter);
    }
}
